// v2.1

import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiv1 from '../api_v1';
import apiContratos, { contratosSafe } from '../api_contratos';
import { surfaceFromHost } from '../surface';
import { capsForSurface, tabsForSurface } from './clientesCaps';
import TroncalCliente from './TroncalCliente';
import PermisosCliente from './PermisosCliente';
import PermisosLineaModal from './PermisosLineaModal';
import QrLineaModal from './QrLineaModal';
import { useConfirm } from "./ConfirmDialog";

/* ── helpers v2.1 ── */
async function safe(fn) { try { return await fn(); } catch { return null; } }

// Estilos compartidos del formulario de nuevo cliente (grid de 2 columnas).
/* Segmento comercial. Espeja `panel/app/segmento.py`, que es el catálogo autoritativo
   (viene de LINKO). Acá está solo como fallback: en runtime se pide a
   GET /v1/onboarding/tipos-cliente para no tener dos verdades. */
const TIPOS_CLIENTE = [
  { codigo:'1', label:'Consumidor final', grupo:'linea'   },
  { codigo:'2', label:'Mayorista',        grupo:'troncal' },
  { codigo:'3', label:'Corporativo',      grupo:'troncal' },
];

const FORM_LABEL = { fontSize:11, color:'var(--text-muted)', display:'block', marginBottom:4 };
const FORM_INPUT = {
  width:'100%', padding:'8px 12px', boxSizing:'border-box', background:'var(--bg-raised)',
  border:'1px solid var(--border)', borderRadius:'var(--r-sm)', color:'var(--text-primary)',
  fontSize:13, fontFamily:'var(--font-ui)', outline:'none',
};

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('es-EC',{day:'2-digit',month:'short',year:'numeric'}); }
  catch { return d; }
}
// Color por familia de evento del log de servicio. Espeja HistorialANI.js: se deriva del
// `tipo` y no de la etiqueta, para que un cambio de texto no rompa el color.
function colorEvento(tipo) {
  if (tipo === 'REGISTRO' || tipo === 'REGISTRO_RECUPERADO' || tipo === 'ALCANZABLE') return '#1e8e3e';
  if (tipo === 'REGISTRO_PERDIDO' || tipo === 'INALCANZABLE') return '#c5221f';
  if (tipo === 'LLAMADA_FALLIDA') return '#e37400';
  if (tipo === 'SIN_MEDICION') return '#80868b';
  return '#1a73e8';   // bloqueos del AGI (permisos, rutas, saldo, antifraude)
}
function fmtDT(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('es-EC',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',hour12:false}); }
  catch { return d; }
}

const STATUS_COLORS = {
  active:    ['var(--success-bg)','var(--success)'],
  pending:   ['var(--info-bg)',   'var(--info)'   ],
  suspended: ['var(--danger-bg)', 'var(--danger)'  ],
  inactive:  ['var(--bg-hover)',  'var(--text-muted)'],
};
function StatusBadge({ s }) {
  const [bg, color] = STATUS_COLORS[s] || STATUS_COLORS.inactive;
  return <span className="nv-badge" style={{ background:bg, color }}>{s||'—'}</span>;
}

// Estado de firma de un documento archivado (traído por el servicio de contratos).
function FirmaBadge({ d }) {
  const por = d?.firmado_por;
  const via = d?.firma_via;
  const at  = d?.firmado_at;
  if (!por || via === 'sin_firma') {
    return <span className="nv-badge nv-badge-muted">Sin firma</span>;
  }
  if (por === 'cliente') {
    return <span className="nv-badge nv-badge-warn" title={fmtDT(at)}>
      Firmado por cliente · pendiente Netvoice{at ? ' · ' + fmtDate(at) : ''}
    </span>;
  }
  if (por === 'netvoice') {
    return <span className="nv-badge nv-badge-info">Firmado por Netvoice</span>;
  }
  if (por === 'ambos') {
    return <span className="nv-badge nv-badge-ok" title={fmtDT(at)}>
      Firmado (cliente + Netvoice){at ? ' · ' + fmtDate(at) : ''}
    </span>;
  }
  return <span className="nv-badge nv-badge-muted">{por}</span>;
}

// Términos estándar visibles del contrato — precarga cuando el cliente aún no tiene
// términos propios guardados (endpoint devuelve terminos:null).
const TERMINOS_DEFAULT = {
  duracion_meses: 12,
  renovacion_automatica: 'yes',
  permanencia_minima: 'no',
  permanencia_meses: null,
  derecho_inscripcion: 60,
  arbitraje: 'no',
  autoriza_llamadas_automaticas: 'no',
  autoriza_guia_telefonica: 'no',
};
const TERMINOS_YESNO = ['renovacion_automatica','permanencia_minima','arbitraje','autoriza_llamadas_automaticas','autoriza_guia_telefonica'];

const MODO_PAGO_LABELS = {
  caja:           'Caja',
  debito_banco:   'Débito bancario',
  debito_tarjeta: 'Débito tarjeta',
  transferencia:  'Transferencia',
};

// La superficie (subdominio) decide capacidades y pestañas: clientes.eneural.org gestiona
// todo salvo Técnico; el panel es solo-lectura salvo Líneas y Técnico. El ROL se cruza
// encima: `implementador` ve lo mismo pero no puede escribir.
//
// Por eso CAPS NO puede ser constante de módulo: el rol llega de /auth/me, después del
// primer render. Cada componente lo calcula con useCaps(). TABS sí es fijo — el
// implementador ve las mismas pestañas.
const SURFACE = surfaceFromHost(typeof window !== 'undefined' ? window.location.hostname : '');
const TABS = tabsForSurface(SURFACE);

// Mientras /auth/me no responde, user es null y capsForSurface no ve rol: devuelve las caps
// de la superficie (con botones). Se muestran de más por un instante, pero el backend es el
// que decide; no hay forma de que un implementador escriba por ganarle a la carga.
function useCaps() {
  const { user } = useAuth();
  return useMemo(() => capsForSurface(SURFACE, user?.role), [user?.role]);
}

/* ── Visor de documento archivado (servicio de contratos) ──
   Se descarga como blob autenticado y se muestra desde memoria porque
   <iframe src="..."> no manda el header de autorización en su pedido. */
function VisorContrato({ documentoId }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let revocar = null;
    (async () => {
      const r = await contratosSafe(() =>
        apiContratos.get(`/documentos/${documentoId}/archivo`, { responseType: 'blob' }));
      if (!r.ok) { setErr('No se pudo abrir el documento'); return; }
      const objectUrl = URL.createObjectURL(r.data);
      revocar = objectUrl;
      setUrl(objectUrl);
    })();
    return () => { if (revocar) URL.revokeObjectURL(revocar); };
  }, [documentoId]);

  if (err) return <div style={{ color:'var(--danger)', fontSize:12 }}>{err}</div>;
  if (!url) return <div style={{ fontSize:12, color:'var(--text-muted)' }}>Cargando documento…</div>;
  return <iframe title="Contrato" src={url} style={{ width:'100%', height:'70vh', border:'none' }} />;
}

/* ── Panel detalle ── */
function ClienteDetail({ id, onClose, bancos = [] }) {
  const CAPS = useCaps();
  // window.confirm no abre cartel en el navegador del panel y devuelve false: cancelaba solo.
  const [confirmar, dialogoConfirm] = useConfirm();
  const [tab, setTab]       = useState('Resumen');
  const [data, setData]     = useState(null);
  const [busy, setBusy]     = useState(true);
  const [dids, setDids]     = useState(null);
  const [contract, setContract]   = useState(null);
  const [payment,  setPayment]    = useState(null);
  const [client,   setClient]     = useState(null);
  const [creds,    setCreds]      = useState(null);
  // Info técnica POR LÍNEA (fila expandible en la pestaña Líneas, solo panel). Mapas por line_id.
  const [techOpen, setTechOpen]   = useState({});   // {line_id: abierta?}
  const [techData, setTechData]   = useState({});   // {line_id: credenciales}
  const [techBusy, setTechBusy]   = useState({});   // {line_id: cargando?}
  const [techErr,  setTechErr]    = useState({});   // {line_id: mensaje de error}
  const [techPass, setTechPass]   = useState({});   // {line_id: mostrar password?}
  const [techSbcBusy, setTechSbcBusy] = useState({}); // {line_id: cambiando SBC?}
  const [techRegBusy, setTechRegBusy] = useState({}); // {line_id: regenerando/guardando pass?}
  const [techEdit,    setTechEdit]    = useState({}); // {line_id: editando pass manual?}
  const [techPwInput, setTechPwInput] = useState({}); // {line_id: pass tipeada}
  const [plansList,     setPlansList]     = useState([]);
  const [editingDetail, setEditingDetail] = useState(false);
  const [detailForm,    setDetailForm]    = useState({});
  const [detailErr,     setDetailErr]     = useState('');
  const [detailBusy,    setDetailBusy]    = useState(false);
  const [didChange,     setDidChange]     = useState(null);    // línea cuyo DID se está cambiando
  const [didChangeVal,  setDidChangeVal]  = useState('');
  const [didChangeMot,  setDidChangeMot]  = useState('');
  const [didChangeBusy, setDidChangeBusy] = useState(false);
  const [didChangeErr,  setDidChangeErr]  = useState('');
  const [didChangeRes,  setDidChangeRes]  = useState(null);    // credenciales nuevas (una vez)
  const [histLine,      setHistLine]      = useState(null);    // línea cuyo historial se ve
  const [hist,          setHist]          = useState([]);
  const [histEv,        setHistEv]        = useState([]);      // log de servicio del ANI
  const [histEvErr,     setHistEvErr]     = useState('');
  const [histReg,       setHistReg]       = useState(null);   // estabilidad del registro
  const [lines,         setLines]         = useState([]);      // líneas del contrato (client_lines)
  const [lineForm,      setLineForm]      = useState({ plan_id:'', did_number:'' });
  const [lineCred,      setLineCred]      = useState(null);    // credenciales SIP de la línea recién creada (una vez)
  const [lineBusy,      setLineBusy]      = useState(false);
  const [lineErr,       setLineErr]       = useState('');
  // Asignar ANI (pestaña Servicios): null | 'registro' | 'uno' (troncal, 1 ANI) | 'pool'.
  const [altaAni,       setAltaAni]       = useState(null);
  const [nTroncales,    setNTroncales]    = useState(null);    // lo informa <TroncalCliente>
  // Un cliente que SOLO tiene troncales no tiene pestaña Permisos: sus permisos se configuran
  // en la fila de cada troncal, porque la config de un servicio va con su entidad. Se decide
  // por lo que el cliente TIENE, no por `tipo_cliente` (solo descriptivo desde 2026-10-20).
  // ⚠️ Va DEBAJO de `client` y `lines`: leerlos antes es el ReferenceError de zona muerta.
  const soloTroncal = (nTroncales ?? client?.n_troncales ?? 0) > 0
                      && (client?.n_lineas ?? 0) === 0 && lines.length === 0;
  const tabsVisibles = TABS.filter(t => !(t === 'Permisos' && soloTroncal));
  useEffect(() => {
    // La ficha se reusa entre clientes sin desmontarse: si venías en Permisos y abrís uno que
    // solo tiene troncales, el tab activo ya no existe y el cuerpo quedaría vacío.
    if (soloTroncal && tab === 'Permisos') setTab('Resumen');
  }, [soloTroncal, tab]);
  // Al cambiar de cliente se cierra cualquier alta de ANI a medio hacer.
  useEffect(() => { setAltaAni(null); setNTroncales(null); }, [id]);
  const [permLine,      setPermLine]      = useState(null);  // línea cuyos permisos se editan (modal)
  const [qrLine,        setQrLine]        = useState(null);  // línea cuyo QR de registro se muestra
  const [editPago,      setEditPago]      = useState(false);  // edición inline del modo de pago / mandato
  const [pagoForm,      setPagoForm]      = useState({ modo_pago:'caja', banco_codigo:'', tipo_cuenta:'AHO', numero_cuenta:'' });
  const [pagoBusy,      setPagoBusy]      = useState(false);
  const [pagoErr,       setPagoErr]       = useState('');
  const [pagoSaved,     setPagoSaved]     = useState(false);
  const [docs,      setDocs]      = useState([]);
  const [docsCaido, setDocsCaido] = useState(false);
  const [verDoc,    setVerDoc]    = useState(null);
  // Acciones de edición del contrato (solo superficie clientes)
  const [contratoAviso, setContratoAviso] = useState(null);   // { kind:'ok'|'err'|'warn'|'info', text }
  const [contratoBusy,  setContratoBusy]  = useState(false);
  const [showTerminos,  setShowTerminos]  = useState(false);
  const [terminosForm,  setTerminosForm]  = useState(null);   // null = cargando dentro del modal
  const [terminosBusy,  setTerminosBusy]  = useState(false);
  const [terminosErr,   setTerminosErr]   = useState('');

  // Recarga SOLO los documentos del contrato (servicio de contratos). Se reutiliza tras
  // regenerar / subir copia firmada / editar términos: como esas operaciones regeneran el
  // contrato en BACKGROUND, no se espera el documento en la respuesta sino que se re-lista.
  const reloadDocs = useCallback(async () => {
    const rc = await contratosSafe(() => apiContratos.get(`/clientes/${id}/documentos`));
    setDocs(rc.data?.data || []);
    setDocsCaido(rc.caido);
  }, [id]);

  const load = useCallback(async () => {
    setBusy(true);
    const [st, di, co, pa, cl, pl, li] = await Promise.all([
      safe(() => apiv1.get(`/onboarding/status/${id}`)),
      safe(() => apiv1.get(`/did/client/${id}`)),
      safe(() => apiv1.get(`/contracts/status/${id}`)),
      safe(() => apiv1.get(`/payments/status/${id}`)),
      safe(() => apiv1.get(`/onboarding/client/${id}`)),
      safe(() => apiv1.get(`/onboarding/plans`)),
      safe(() => apiv1.get(`/client/${id}/lines`)),
    ]);
    setData(st?.data || null);
    setDids(di?.data || null);
    setContract(co?.data || null);
    setPayment(pa?.data || null);
    const clientData = cl?.data || null;
    setClient(clientData);
    setPlansList(pl?.data?.data || []);
    // /client/{id}/lines devuelve TODAS las filas de client_lines, y una troncal tambien es
    // una (auth_mode='ip'). Sin filtrar, la ficha de un cliente de troncal listaba sus
    // propias troncales en la tabla de lineas, rotuladas "no corresponden a este cliente".
    setLines((li?.data?.data || []).filter(l => l.auth_mode !== 'ip'));
    // Cargar credenciales SIP completas (solo si el servidor las devuelve)
    try {
      const cr = await apiv1.get(`/activation/credentials/${id}`);
      setCreds(cr.data);
    } catch(e) {
      setCreds(null);
    }
    // Se consulta APARTE del Promise.all de la ficha: si el servicio de contratos no
    // responde, el resto de las pestañas debe cargar igual.
    await reloadDocs();
    setBusy(false);
  }, [id, reloadDocs]);

  useEffect(() => { load(); }, [load]);

  // Info técnica POR LÍNEA: abre/cierra la fila expandible y trae las credenciales de
  // esa línea (GET /v1/lines/{line_id}/credentials) la primera vez que se abre.
  const toggleLineTech = async (lineId) => {
    const willOpen = !techOpen[lineId];
    setTechOpen(o => ({ ...o, [lineId]: willOpen }));
    if (!willOpen || techData[lineId]) return;      // ya cargada o cerrando
    setTechBusy(b => ({ ...b, [lineId]: true }));
    setTechErr(e => ({ ...e, [lineId]: '' }));
    try {
      const r = await apiv1.get(`/lines/${lineId}/credentials`);
      setTechData(d => ({ ...d, [lineId]: r.data }));
    } catch (e) {
      setTechErr(er => ({ ...er, [lineId]:
        e.response?.status === 403 ? 'Sin permisos para ver credenciales' :
        e.response?.status === 404 ? 'La línea no tiene credenciales SIP activas' :
        'Error al cargar la información técnica' }));
    } finally {
      setTechBusy(b => ({ ...b, [lineId]: false }));
    }
  };

  // Activa/desactiva el registro vía SBC (internet) de UNA línea y re-carga su panel.
  const toggleLineSbc = async (lineId, enable) => {
    setTechSbcBusy(b => ({ ...b, [lineId]: true }));
    try {
      await apiv1.post(`/lines/${lineId}/sbc`, { enable });
      const r = await apiv1.get(`/lines/${lineId}/credentials`);
      setTechData(d => ({ ...d, [lineId]: r.data }));
    } catch (e) {
      alert('No se pudo cambiar el registro SBC: ' + (e.response?.data?.detail || e.message || 'error'));
    } finally {
      setTechSbcBusy(b => ({ ...b, [lineId]: false }));
    }
  };

  // Regenera (aleatoria) o FIJA (manual) la password SIP de una línea y re-carga su panel.
  // pass=null → regenerar; pass=string → fijar la que tipeó el técnico de soporte.
  const setLinePass = async (lineId, pass) => {
    if (pass === null && !(await confirmar(
      'Se generará una contraseña SIP NUEVA (deja de funcionar la anterior).\n' +
      'El cliente deberá reconfigurar su dispositivo.\n\n¿Continuar?'))) return;
    if (pass !== null) {
      const pw = pass.trim();
      if (pw.length < 6 || pw.length > 64 || /\s/.test(pw)) {
        alert('La contraseña debe tener entre 6 y 64 caracteres, sin espacios.'); return;
      }
    }
    setTechRegBusy(b => ({ ...b, [lineId]: true }));
    try {
      await apiv1.post(`/lines/${lineId}/regenerate-credentials`,
        pass === null ? {} : { password: pass.trim() });
      const r = await apiv1.get(`/lines/${lineId}/credentials`);
      setTechData(d => ({ ...d, [lineId]: r.data }));
      setTechPass(p => ({ ...p, [lineId]: true }));
      setTechEdit(e => ({ ...e, [lineId]: false }));
      setTechPwInput(i => ({ ...i, [lineId]: '' }));
    } catch (e) {
      alert('No se pudo cambiar la contraseña: ' + (e.response?.data?.detail || e.message || 'error'));
    } finally {
      setTechRegBusy(b => ({ ...b, [lineId]: false }));
    }
  };

  // Panel técnico de UNA línea (dentro de la fila expandida). Incluye el toggle SBC
  // (registro por internet vs LAN) y la gestión de la contraseña (regenerar / editar).
  const renderLineTech = (cred, lineId) => (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 20px' }}>
      {[
        ['Número DID',        cred.did_number || '— sin asignar'],
        ['Servidor SIP',      cred.sip_server],
        ['Puerto',            cred.sip_port],
        ['Realm / Dominio',   cred.sip_realm],
        ['Usuario SIP',       cred.sip_user],
        ['Codecs',            cred.codecs || '—'],
        ['Canales máx.',      cred.max_channels],
        ['Registro SIP',      cred.is_registered ? '● Registrado' : '○ No registra'],
        ['Registrando desde', cred.reg_source ? (cred.reg_source + ' (' + cred.reg_source_class + ')') : '—'],
        ['Registra hacia',    cred.reg_via || '—'],
        ['Última reg.',       cred.last_register ? new Date(cred.last_register).toLocaleString('es-EC') : '— no registra'],
        ['Activado',          cred.activated_at ? new Date(cred.activated_at).toLocaleDateString('es-EC') : '—'],
      ].map(([lbl,val]) => (
        <div key={lbl} style={{ padding:'6px 0', borderBottom:'1px solid var(--border-subtle)' }}>
          <div style={{ fontSize:9, textTransform:'uppercase', letterSpacing:'.07em', color:'var(--text-muted)', marginBottom:2 }}>{lbl}</div>
          <div style={{ fontSize:12, color:'var(--text-primary)', fontWeight:500, fontFamily:'var(--font-mono)' }}>{val || '—'}</div>
        </div>
      ))}
      <div style={{ padding:'10px 0', borderBottom:'1px solid var(--border-subtle)', gridColumn:'1/-1' }}>
        <label style={{ display:'flex', alignItems:'center', gap:8, cursor: techSbcBusy[lineId]?'default':'pointer' }}>
          <input type="checkbox" checked={!!cred.sbc} disabled={!!techSbcBusy[lineId]}
            onChange={e => toggleLineSbc(lineId, e.target.checked)} />
          <span style={{ fontSize:12, fontWeight:600, color:'var(--text-primary)' }}>
            Registrar por internet (vía SBC){techSbcBusy[lineId] ? ' …' : ''}
          </span>
        </label>
        <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:4, lineHeight:1.5 }}>
          {cred.sbc
            ? <>Registra por la <b>IP pública</b> (<span style={{ fontFamily:'var(--font-mono)' }}>mi.eneural.org</span>) con auth digest, provisto en el SBC (Kamailio).</>
            : <>Registra por la <b>IP LAN</b> (<span style={{ fontFamily:'var(--font-mono)' }}>192.168.182.20</span>). Activá para registro público por internet vía SBC.</>}
        </div>
      </div>
      <div style={{ padding:'6px 0', borderBottom:'1px solid var(--border-subtle)', gridColumn:'1/-1' }}>
        <div style={{ fontSize:9, textTransform:'uppercase', letterSpacing:'.07em', color:'var(--text-muted)', marginBottom:4 }}>Password SIP</div>
        {techEdit[lineId] ? (
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            <input value={techPwInput[lineId] || ''} disabled={!!techRegBusy[lineId]}
              onChange={e => setTechPwInput(i => ({ ...i, [lineId]: e.target.value }))}
              placeholder="nueva contraseña (6-64, sin espacios)"
              style={{ flex:1, minWidth:180, fontSize:13, fontFamily:'var(--font-mono)', padding:'5px 8px', borderRadius:4, border:'1px solid var(--border)', background:'var(--bg-raised)', color:'var(--text-primary)' }} />
            <button onClick={() => setLinePass(lineId, techPwInput[lineId] || '')} disabled={!!techRegBusy[lineId]}
              style={{ fontSize:11, padding:'4px 10px', borderRadius:4, border:'1px solid var(--brand)', background:'var(--brand)', color:'#fff', cursor: techRegBusy[lineId]?'default':'pointer', fontWeight:600 }}>
              {techRegBusy[lineId] ? '…' : 'Guardar'}
            </button>
            <button onClick={() => { setTechEdit(e => ({ ...e, [lineId]: false })); setTechPwInput(i => ({ ...i, [lineId]: '' })); }} disabled={!!techRegBusy[lineId]}
              style={{ fontSize:11, padding:'4px 10px', borderRadius:4, border:'1px solid var(--border)', background:'var(--bg-raised)', color:'var(--text-secondary)', cursor:'pointer' }}>
              Cancelar
            </button>
          </div>
        ) : (
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            {cred.password_retrievable === false ? (
              <span style={{ fontSize:11, color:'var(--text-muted)', flex:1, lineHeight:1.4, minWidth:160 }}>
                🔒 No recuperable (solo hash). Regenerá o editá para fijar una nueva.
              </span>
            ) : (
              <>
                <span style={{ fontSize:13, color:'var(--text-primary)', fontWeight:700, fontFamily:'var(--font-mono)', flex:1, letterSpacing:'0.05em', minWidth:140 }}>
                  {techPass[lineId] ? cred.sip_password : '••••••••••••••••'}
                </span>
                <button onClick={() => setTechPass(p => ({ ...p, [lineId]: !p[lineId] }))}
                  style={{ fontSize:11, padding:'4px 10px', borderRadius:4, border:'1px solid var(--border)', background:'var(--bg-raised)', color:'var(--text-secondary)', cursor:'pointer' }}>
                  {techPass[lineId] ? 'Ocultar' : 'Mostrar'}
                </button>
                <button onClick={() => navigator.clipboard.writeText(cred.sip_password)}
                  style={{ fontSize:11, padding:'4px 10px', borderRadius:4, border:'1px solid var(--border)', background:'var(--bg-raised)', color:'var(--text-secondary)', cursor:'pointer' }}>
                  Copiar
                </button>
              </>
            )}
            <button onClick={() => { setTechEdit(e => ({ ...e, [lineId]: true })); setTechPwInput(i => ({ ...i, [lineId]: '' })); }} disabled={!!techRegBusy[lineId]}
              style={{ fontSize:11, padding:'4px 10px', borderRadius:4, border:'1px solid var(--border)', background:'var(--bg-raised)', color:'var(--text-secondary)', cursor:'pointer' }}>
              ✎ Editar
            </button>
            <button onClick={() => setLinePass(lineId, null)} disabled={!!techRegBusy[lineId]}
              title="Genera una contraseña nueva (invalida la anterior)"
              style={{ fontSize:11, padding:'4px 10px', borderRadius:4, border:'1px solid var(--warning,#d97706)', background:'var(--bg-raised)', color:'var(--warning,#d97706)', cursor: techRegBusy[lineId]?'default':'pointer', whiteSpace:'nowrap' }}>
              {techRegBusy[lineId] ? '…' : '🔄 Regenerar'}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  /* ── Acciones de edición del contrato (solo superficie clientes) ── */

  // Nombre visible del cliente para el metadato del documento archivado.
  const nombreCliente = () =>
    client?.razon_social ||
    [client?.primer_nombre, client?.segundo_nombre, client?.primer_apellido, client?.segundo_apellido]
      .filter(Boolean).join(' ') || '';

  const errText = (e) => e?.response?.data?.detail || e?.message || 'error';

  // Regenerar el contrato. La regeneración corre en BACKGROUND en el backend: tras el 200
  // se avisa y se re-listan los documentos a los ~4s (no se espera el contrato en la respuesta).
  const handleRegenerar = async () => {
    if (contratoBusy) return;
    setContratoBusy(true);
    setContratoAviso({ kind:'info', text:'Regenerando contrato…' });
    try {
      await apiv1.post(`/client/${id}/contrato/regenerar`);
      setTimeout(async () => {
        await reloadDocs();
        setContratoBusy(false);
        setContratoAviso({ kind:'ok', text:'Contrato regenerado.' });
      }, 4000);
    } catch (e) {
      setContratoBusy(false);
      setContratoAviso({ kind:'err', text:'No se pudo regenerar: ' + errText(e) });
    }
  };

  // Subir una copia firmada (PDF escaneado) al archivo del contrato.
  const handleSubirFirmada = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';   // permite reintentar el mismo archivo
    if (!file || contratoBusy) return;
    setContratoBusy(true);
    setContratoAviso({ kind:'info', text:'Subiendo copia firmada…' });
    const form = new FormData();
    form.append('archivo', file);
    form.append('cliente_nombre', nombreCliente());
    form.append('numero_contrato', client?.numero_contrato || '');
    form.append('tipo', 'contrato');
    form.append('firma_via', 'manuscrita_escaneada');
    form.append('firmado_por', 'cliente');
    try {
      // apiContratos directo (no contratosSafe) para distinguir el 409 por duplicado.
      await apiContratos.post(`/clientes/${id}/documentos`, form);
      await reloadDocs();
      setContratoAviso({ kind:'ok', text:'Copia firmada archivada.' });
    } catch (e) {
      if (e?.response?.status === 409) {
        setContratoAviso({ kind:'warn', text:'Ese archivo ya está archivado (mismo contenido).' });
      } else {
        setContratoAviso({ kind:'err', text:'No se pudo subir: ' + errText(e) });
      }
    } finally {
      setContratoBusy(false);
    }
  };

  // Abrir el modal de términos, precargando los términos del cliente (o el estándar si no tiene).
  // Se usa try/catch directo (NO safe()) para distinguir un FALLO de carga (403/500/red) de
  // "el cliente no tiene términos propios" (terminos:null explícito). Un fallo NO debe caer a los
  // estándar: guardar pisaría términos reales ya negociados.
  const openTerminos = async () => {
    setTerminosErr('');
    setTerminosBusy(false);
    setTerminosForm(null);   // estado "cargando" dentro del modal
    setShowTerminos(true);
    try {
      const r = await apiv1.get(`/client/${id}/contrato/terminos`);
      const t = r.data?.terminos;   // null explícito = sin términos propios → estándar
      // El backend podría devolver los sí/no como boolean: normalizar sobre `t` antes del merge.
      if (t) {
        TERMINOS_YESNO.forEach(k => {
          if (t[k] === true)  t[k] = 'yes';
          else if (t[k] === false) t[k] = 'no';
        });
      }
      setTerminosForm(t ? { ...TERMINOS_DEFAULT, ...t } : { ...TERMINOS_DEFAULT });
    } catch {
      setTerminosErr('No se pudieron cargar los términos actuales. Cerrá y reintentá.');
    }
  };

  // Guardar términos → el backend regenera el contrato en BACKGROUND (no se espera aquí).
  const saveTerminos = async () => {
    if (!terminosForm) return;
    setTerminosBusy(true);
    setTerminosErr('');
    const num = (v) => (v === '' || v == null ? null : Number(v));
    const payload = {
      ...terminosForm,
      duracion_meses:      num(terminosForm.duracion_meses),
      permanencia_meses:   num(terminosForm.permanencia_meses),
      derecho_inscripcion: num(terminosForm.derecho_inscripcion),
    };
    try {
      await apiv1.put(`/client/${id}/contrato/terminos`, payload);
      setShowTerminos(false);
      setContratoBusy(true);
      setContratoAviso({ kind:'info', text:'Términos guardados, regenerando…' });
      setTimeout(async () => {
        await reloadDocs();
        setContratoBusy(false);
        setContratoAviso({ kind:'ok', text:'Contrato regenerado con los nuevos términos.' });
      }, 4000);
    } catch (e) {
      setTerminosErr('No se pudo guardar: ' + errText(e));
    } finally {
      setTerminosBusy(false);
    }
  };

  if (busy) return (
    <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div className="nv-loading"><span className="nv-spinner" /><span>Cargando cliente...</span></div>
    </div>
  );

  const steps = data?.steps || {};
  const progress = data?.progress || '0/0';

  const handleSaveDetail = async () => {
    if (!detailForm.name?.trim()) return setDetailErr('El nombre es obligatorio');
    setDetailBusy(true); setDetailErr('');
    try {
      await apiv1.patch(`/onboarding/client/${id}`, detailForm);
      setEditingDetail(false);
      load();
    } catch(e) { setDetailErr(e.response?.data?.detail || 'Error al guardar'); }
    finally { setDetailBusy(false); }
  };

  // Retirados con el modelo multi-línea: handleChangePlan / handleActivate / handleSaveDid
  // escribían en clients.plan_id y clients.did_number, obsoletos desde que la línea es la
  // unidad (DID + plan + SIP). El alta de líneas vive en handleAddLine.

  const handleAddLine = async () => {
    if (!lineForm.plan_id) return setLineErr('El plan es obligatorio');
    setLineBusy(true); setLineErr(''); setLineCred(null);
    try {
      const body = { plan_id: lineForm.plan_id };
      if (lineForm.did_number?.trim()) body.did_number = lineForm.did_number.trim();
      const r = await apiv1.post(`/client/${id}/lines`, body);
      setLineCred({ sip_user: r.data.sip_user, sip_password: r.data.sip_password });
      setLineForm({ plan_id:'', did_number:'' });
      load();
    } catch(e) { setLineErr(e.response?.data?.detail || 'Error al agregar la línea'); }
    finally { setLineBusy(false); }
  };

  const openDidChange = (l) => {
    setDidChange(l); setDidChangeVal(''); setDidChangeMot('');
    setDidChangeErr(''); setDidChangeRes(null);
  };

  const handleChangeDid = async () => {
    if (!didChangeVal.trim()) return setDidChangeErr('Ingresa el nuevo DID');
    setDidChangeBusy(true); setDidChangeErr('');
    try {
      const r = await apiv1.patch(`/lines/${didChange.id}/did`,
        { did_number: didChangeVal.trim(), motivo: didChangeMot.trim() || null });
      setDidChangeRes(r.data);
      load();
    } catch(e) { setDidChangeErr(e.response?.data?.detail || 'Error al cambiar el DID'); }
    finally { setDidChangeBusy(false); }
  };

  const openDidHistory = async (l) => {
    setHistLine(l); setHist([]); setHistEv([]); setHistEvErr(''); setHistReg(null);
    // Dos fuentes distintas en la misma ficha: los cambios de número (quién lo asignó) y el
    // log de servicio del ANI (registros, bloqueos, llamadas fallidas), que sale de la vista
    // netvoice.ani_historial ya redactado en castellano por el backend.
    const [h, ev] = await Promise.all([
      safe(() => apiv1.get(`/lines/${l.id}/did-history`)),
      l.did_number
        ? apiv1.get(`/ani/${encodeURIComponent(l.did_number)}/historial`,
                    { params: { dias: 90, limit: 100 } }).catch(e => ({ __err: e }))
        : null,
    ]);
    setHist(h?.data?.data || []);
    if (ev?.__err) {
      // El log es herramienta técnica: sac e implementador no lo ven (403). No es una falla.
      setHistEvErr(ev.__err.response?.status === 403
        ? 'Tu perfil no tiene acceso al log de servicio.'
        : 'No se pudo leer el log de servicio.');
    } else {
      setHistEv(ev?.data?.eventos || []);
      setHistReg(ev?.data?.registro || null);
    }
  };

  const handleRemoveLine = async (lineId, did) => {
    if (!(await confirmar(`¿Quitar la línea ${did}? Se elimina su cuenta SIP y se libera el DID.`))) return;
    try {
      await apiv1.delete(`/lines/${lineId}`);
      load();
    } catch(e) { alert(e.response?.data?.detail || 'Error al quitar la línea'); }
  };

  // Guarda modo de pago (+ mandato si es débito bancario). El número de cuenta
  // sólo se manda si el usuario escribió uno nuevo — nunca vuelve del backend.
  const handleSavePago = async () => {
    setPagoBusy(true); setPagoErr(''); setPagoSaved(false);
    try {
      const body = { modo_pago: pagoForm.modo_pago };
      if (pagoForm.modo_pago === 'debito_banco') {
        body.banco_codigo = pagoForm.banco_codigo;
        body.tipo_cuenta  = pagoForm.tipo_cuenta;
        if (pagoForm.numero_cuenta.trim()) body.numero_cuenta = pagoForm.numero_cuenta.trim();
      }
      await apiv1.put(`/onboarding/client/${id}/metodo-pago`, body);
      setEditPago(false);
      setPagoSaved(true);
      setTimeout(() => setPagoSaved(false), 3000);
      load();
    } catch(e) { setPagoErr(e?.response?.data?.detail || 'Error'); }
    finally { setPagoBusy(false); }
  };

  function renderTab() {
    switch(tab) {
      case 'Resumen': return (
        <div>
          {/* Progress bar onboarding */}
          <div className="nv-card" style={{ marginBottom:12 }}>
            <div className="nv-card-header">
              <span className="nv-card-title">Progreso onboarding</span>
              <span style={{ fontFamily:'var(--font-mono)', fontSize:12, color:'var(--brand)' }}>{progress}</span>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:8 }}>
              {Object.entries(steps).map(([k,v]) => (
                <div key={k} style={{
                  display:'flex', alignItems:'center', gap:8,
                  padding:'8px 10px',
                  background: v ? 'var(--success-bg)' : 'var(--bg-raised)',
                  borderRadius:'var(--r-sm)',
                  border:`1px solid ${v ? 'rgba(0,201,141,0.2)' : 'var(--border)'}`,
                }}>
                  <span style={{ fontSize:14, color: v ? 'var(--success)' : 'var(--text-muted)' }}>{v ? '✓' : '○'}</span>
                  <span style={{ fontSize:11, color: v ? 'var(--success)' : 'var(--text-muted)' }}>
                    {k.replace(/_/g,' ')}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Datos del cliente — con edición inline */}
          <div className="nv-card">
            <div className="nv-card-header">
              <span className="nv-card-title">Datos del cliente</span>
              {CAPS.editDatos && !editingDetail && (
                <button onClick={() => {
                  setDetailForm({
                    primer_nombre:    client?.primer_nombre||'',
                    segundo_nombre:   client?.segundo_nombre||'',
                    primer_apellido:  client?.primer_apellido||'',
                    segundo_apellido: client?.segundo_apellido||'',
                    razon_social:     client?.razon_social||'',
                    ruc: client?.ruc||'',
                    phone: client?.phone||'', celular: client?.celular||'',
                    provincia: client?.provincia||'', ciudad: client?.ciudad||'',
                    direccion: client?.direccion||'',
                    tipo_identificacion: client?.tipo_identificacion||'',
                    tipo_cuenta: client?.tipo_cuenta||'',
                    observaciones: client?.observaciones||'',
                  });
                  setDetailErr(''); setEditingDetail(true);
                }}
                style={{ fontSize:11, padding:'3px 10px', borderRadius:4,
                  border:'1px solid var(--border)', background:'var(--bg-raised)',
                  color:'var(--text-secondary)', cursor:'pointer' }}>
                  ✏ Editar
                </button>
              )}
            </div>
            {editingDetail ? (
              <div>
                {[
                  { label:'1er Nombre',           key:'primer_nombre',      placeholder:'Ej: Juan' },
                  { label:'2do Nombre',           key:'segundo_nombre',     placeholder:'Ej: Carlos' },
                  { label:'1er Apellido',         key:'primer_apellido',    placeholder:'Ej: Pérez' },
                  { label:'2do Apellido',         key:'segundo_apellido',   placeholder:'Ej: Gómez' },
                  { label:'Razón social',         key:'razon_social',       placeholder:'Solo empresas' },
                  { label:'RUC / Cédula',         key:'ruc',                placeholder:'Ej: 0912345678001' },
                  { label:'Teléfono fijo',        key:'phone',              placeholder:'Ej: 042123456' },
                  { label:'Celular',              key:'celular',            placeholder:'Ej: 0991234567' },
                  { label:'Ciudad',               key:'ciudad',             placeholder:'Ej: Guayaquil' },
                  { label:'Dirección',            key:'direccion',          placeholder:'Ej: Av. 9 de Octubre 123' },
                  { label:'Tipo identificación',  key:'tipo_identificacion',placeholder:'C=cédula / R=RUC' },
                  { label:'Tipo cuenta',          key:'tipo_cuenta',        placeholder:'empresarial / personal' },
                  { label:'Observaciones',        key:'observaciones',      placeholder:'Notas internas...' },
                ].map(({ label, key, placeholder }) => (
                  <div key={key} style={{ marginBottom:10 }}>
                    <label style={{ fontSize:10, color:'var(--text-muted)', display:'block', marginBottom:3 }}>{label}</label>
                    <input type="text" value={detailForm[key]||''} placeholder={placeholder}
                      onChange={e => setDetailForm(f => ({ ...f, [key]: e.target.value }))}
                      style={{ width:'100%', padding:'6px 10px', boxSizing:'border-box',
                        background:'var(--bg-raised)', border:'1px solid var(--border)',
                        borderRadius:'var(--r-sm)', color:'var(--text-primary)', fontSize:12, outline:'none' }} />
                  </div>
                ))}
                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:10, color:'var(--text-muted)', display:'block', marginBottom:3 }}>Provincia</label>
                  <select value={detailForm.provincia||''} onChange={e => setDetailForm(f => ({ ...f, provincia: e.target.value }))}
                    style={{ width:'100%', padding:'6px 10px', boxSizing:'border-box',
                      background:'var(--bg-raised)', border:'1px solid var(--border)',
                      borderRadius:'var(--r-sm)', color:'var(--text-primary)', fontSize:12, outline:'none' }}>
                    <option value="">— Sin provincia —</option>
                    {['AZUAY','BOLIVAR','CHIMBORAZO','COTOPAXI','EL ORO','ESMERALDAS','GUAYAS',
                      'IMBABURA','LOJA','LOS RIOS','MANABI','PASTAZA','PICHINCHA',
                      'SANTA ELENA','SANTO DOMINGO','SUCUMBIOS','TENA','TUNGURAHUA'].map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                {detailErr && (
                  <div style={{ padding:'6px 10px', background:'var(--danger-bg)', color:'var(--danger)',
                    borderRadius:'var(--r-sm)', fontSize:11, marginBottom:10 }}>{detailErr}</div>
                )}
                <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                  <button onClick={() => setEditingDetail(false)} disabled={detailBusy}
                    style={{ fontSize:11, padding:'4px 14px', borderRadius:4, border:'1px solid var(--border)',
                      background:'transparent', color:'var(--text-secondary)', cursor:'pointer' }}>
                    Cancelar
                  </button>
                  <button onClick={handleSaveDetail} disabled={detailBusy}
                    style={{ fontSize:11, padding:'4px 14px', borderRadius:4, border:'none',
                      background:'var(--brand)', color:'#fff', cursor:'pointer' }}>
                    {detailBusy ? 'Guardando...' : 'Guardar cambios'}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 20px' }}>
                {[
                  ['RUC / Cédula',   client?.ruc || '—'],
                  ['Estado',         <StatusBadge s={client?.status || data?.status} />],
                  ['Empresa', empresaDeOrigin(client?.origin || data?.origin)],
                  ['1er Nombre',     client?.primer_nombre || '—'],
                  ['2do Nombre',     client?.segundo_nombre || '—'],
                  ['1er Apellido',   client?.primer_apellido || '—'],
                  ['2do Apellido',   client?.segundo_apellido || '—'],
                  ['Razón social',   client?.razon_social || '—'],
                  ['Teléfono',       client?.phone || '—'],
                  ['Celular',        client?.celular || '—'],
                  ['Provincia',      client?.provincia || '—'],
                  ['Ciudad',         client?.ciudad || '—'],
                  ['Dirección',      client?.direccion || '—'],
                  ['Tipo cuenta',    client?.tipo_cuenta || '—'],
                  ['Observaciones',  client?.observaciones || '—'],
                  ['Registrado',     fmtDate(client?.created_at || data?.created_at)],
                  ['Actualizado',    fmtDate(client?.updated_at)],
                ].map(([lbl,val]) => (
                  <div key={lbl} style={{ padding:'6px 0', borderBottom:'1px solid var(--border-subtle)' }}>
                    <div style={{ fontSize:9, textTransform:'uppercase', letterSpacing:'.07em', color:'var(--text-muted)', marginBottom:2 }}>{lbl}</div>
                    <div style={{ fontSize:12, color:'var(--text-primary)', fontWeight:500 }}>{val}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );

      case 'Comercial': return (
        <div>
          <div className="nv-card" style={{ marginBottom:12 }}>
            <div className="nv-card-header"><span className="nv-card-title">Información comercial</span></div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 20px' }}>
              {[
                ['Contrato',            client?.numero_contrato || '—'],
                ['Líneas contratadas',  lines.length || '—'],
                ['Renta mensual total', lines.length
                    ? `$${lines.reduce((s,l) => s + (l.es_prueba === 'yes' ? 0 : parseFloat(l.monthly_fee||0)), 0).toFixed(2)}`
                    : '—'],
                ['Estado',              <StatusBadge s={client?.status || data?.status} />],
                ['Tipo identificación', client?.tipo_identificacion || '—'],
                ['Email verificado',    client?.email_verified ? 'Sí' : 'No'],
              ].map(([lbl,val]) => (
                <div key={lbl} style={{ padding:'6px 0', borderBottom:'1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize:9, textTransform:'uppercase', letterSpacing:'.07em', color:'var(--text-muted)', marginBottom:2 }}>{lbl}</div>
                  <div style={{ fontSize:12, color:'var(--text-primary)', fontWeight:500 }}>{val}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Planes por línea: el plan es de la línea, no del cliente. Una factura por contrato. */}
          <div className="nv-card" style={{ marginBottom:12 }}>
            <div className="nv-card-header"><span className="nv-card-title">Planes contratados por línea</span></div>
            {lines.length === 0 ? (
              <div style={{ fontSize:12, color:'var(--text-muted)', padding:'8px 0' }}>
                Sin líneas. Agrega una en la pestaña <strong>Líneas</strong>.
              </div>
            ) : (
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr style={{ color:'var(--text-muted)', fontSize:9, textTransform:'uppercase', letterSpacing:'.07em' }}>
                    <th style={{ textAlign:'left',  padding:'4px 0' }}>DID / ANI</th>
                    <th style={{ textAlign:'left',  padding:'4px 0' }}>Plan</th>
                    <th style={{ textAlign:'right', padding:'4px 0' }}>Renta</th>
                    <th style={{ textAlign:'left',  padding:'4px 0 4px 12px' }}>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map(l => (
                    <tr key={l.id} style={{ borderTop:'1px solid var(--border-subtle)' }}>
                      <td style={{ padding:'6px 0', fontFamily:'var(--font-mono)', color:'var(--brand)' }}>{l.did_number}</td>
                      <td style={{ padding:'6px 0' }}>
                        {l.plan_nombre || '—'}
                        {l.es_prueba === 'yes' && (
                          <span style={{ marginLeft:6, fontSize:9, padding:'1px 6px', borderRadius:8,
                            background:'var(--warning-bg,#3a2a10)', color:'var(--warning,#d97706)' }}>sin facturación</span>
                        )}
                      </td>
                      <td style={{ padding:'6px 0', textAlign:'right', fontFamily:'var(--font-mono)' }}>
                        {l.es_prueba === 'yes' ? '—' : `$${parseFloat(l.monthly_fee||0).toFixed(2)}`}
                      </td>
                      <td style={{ padding:'6px 0 6px 12px' }}><StatusBadge s={l.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {/* Cobranza / Modo de pago — con edición inline del mandato */}
          <div className="nv-card" style={{ marginBottom:12 }}>
            <div className="nv-card-header">
              <span className="nv-card-title">Cobranza / Modo de pago</span>
              {CAPS.editPago && !editPago && (
                <button onClick={() => {
                    setPagoForm({
                      modo_pago:     client?.modo_pago || 'caja',
                      banco_codigo:  client?.mandato?.banco_codigo || '',
                      tipo_cuenta:   client?.mandato?.tipo_cuenta || 'AHO',
                      numero_cuenta: '',
                    });
                    setPagoErr(''); setEditPago(true);
                  }}
                  style={{ fontSize:11, padding:'3px 10px', borderRadius:4,
                    border:'1px solid var(--border)', background:'var(--bg-raised)',
                    color:'var(--text-secondary)', cursor:'pointer' }}>
                  ✎ Editar
                </button>
              )}
            </div>
            {editPago ? (
              <div>
                <div style={{ marginBottom:10 }}>
                  <label style={{ fontSize:10, color:'var(--text-muted)', display:'block', marginBottom:3 }}>Modo de pago</label>
                  <select className="nv-select" value={pagoForm.modo_pago}
                    onChange={e => setPagoForm(f => ({ ...f, modo_pago: e.target.value }))}>
                    <option value="caja">Caja</option>
                    <option value="debito_banco">Débito bancario</option>
                    <option value="debito_tarjeta">Débito tarjeta</option>
                    <option value="transferencia">Transferencia</option>
                  </select>
                </div>
                {pagoForm.modo_pago === 'debito_banco' && (
                  <>
                    <div style={{ marginBottom:10 }}>
                      <label style={{ fontSize:10, color:'var(--text-muted)', display:'block', marginBottom:3 }}>Banco</label>
                      <select className="nv-select" value={pagoForm.banco_codigo}
                        onChange={e => setPagoForm(f => ({ ...f, banco_codigo: e.target.value }))}>
                        <option value="">— Seleccione banco —</option>
                        {bancos.map(b => (
                          <option key={b.codigo_bce} value={b.codigo_bce}>{b.nombre}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ marginBottom:10 }}>
                      <label style={{ fontSize:10, color:'var(--text-muted)', display:'block', marginBottom:3 }}>Tipo de cuenta</label>
                      <select className="nv-select" value={pagoForm.tipo_cuenta}
                        onChange={e => setPagoForm(f => ({ ...f, tipo_cuenta: e.target.value }))}>
                        <option value="AHO">Ahorros</option>
                        <option value="CTE">Corriente</option>
                      </select>
                    </div>
                    <div style={{ marginBottom:10 }}>
                      <label style={{ fontSize:10, color:'var(--text-muted)', display:'block', marginBottom:3 }}>N° de cuenta</label>
                      <input className="nv-input" value={pagoForm.numero_cuenta}
                        onChange={e => setPagoForm(f => ({ ...f, numero_cuenta: e.target.value }))}
                        placeholder="nueva cuenta; se guarda cifrada" />
                    </div>
                  </>
                )}
                {pagoErr && (
                  <div style={{ padding:'6px 10px', background:'var(--danger-bg)', color:'var(--danger)',
                    borderRadius:'var(--r-sm)', fontSize:11, marginBottom:10 }}>{pagoErr}</div>
                )}
                <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                  <button className="nv-btn nv-btn-secondary nv-btn-sm" disabled={pagoBusy}
                    onClick={() => { setEditPago(false); setPagoErr(''); }}>Cancelar</button>
                  <button className="nv-btn nv-btn-primary nv-btn-sm" disabled={pagoBusy}
                    onClick={handleSavePago}>{pagoBusy ? '…' : 'Guardar'}</button>
                </div>
              </div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 20px' }}>
                {[
                  ['Modo de pago',     MODO_PAGO_LABELS[client?.modo_pago] || client?.modo_pago || 'Caja'],
                  ['Mandato bancario', client?.mandato
                    ? `Banco: ${client.mandato.banco_nombre} · ${client.mandato.tipo_cuenta} · ****${client.mandato.ultimos4}`
                    : '—'],
                ].map(([lbl,val]) => (
                  <div key={lbl} style={{ padding:'6px 0', borderBottom:'1px solid var(--border-subtle)' }}>
                    <div style={{ fontSize:9, textTransform:'uppercase', letterSpacing:'.07em', color:'var(--text-muted)', marginBottom:2 }}>{lbl}</div>
                    <div style={{ fontSize:12, color:'var(--text-primary)', fontWeight:500 }}>{val}</div>
                  </div>
                ))}
              </div>
            )}
            {pagoSaved && (
              <div style={{ marginTop:10, padding:'6px 10px', background:'var(--success-bg)',
                color:'var(--success)', borderRadius:'var(--r-sm)', fontSize:11, fontWeight:600 }}>
                ✓ Modo de pago actualizado
              </div>
            )}
          </div>
          {/* El plan ya no se asigna a nivel cliente: cada línea lleva el suyo (pestaña Líneas).
              clients.plan_id quedó obsoleto con el modelo de contrato multi-línea. */}
        </div>
      );

      case 'Líneas': return (
        <div>
          {/* Servicios del cliente. El tipo de cliente es solo descriptivo (2026-10-20): como en
              Denwa, cualquier cliente puede tener ANIs con registro (líneas) y troncales IP
              con uno o varios ANIs a la vez. Todo se asigna desde «Asignar ANI». */}
          <div className="nv-card" style={{ marginBottom:12 }}>
            <div className="nv-card-header"><span className="nv-card-title">Asignar ANI</span></div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {[
                ['registro', 'ANI con registro', 'El equipo se registra con usuario y contraseña SIP', true],
                ['uno',      'ANI por troncal', 'Una troncal IP con un solo ANI', CAPS.manageTroncal],
                ['pool',     'Pool de ANIs por troncal', 'Una troncal IP con un bloque de ANIs', CAPS.manageTroncal],
              ].filter(([, , , ok]) => ok).map(([k, label, hint]) => (
                <button key={k} title={hint}
                  className={'nv-btn ' + (altaAni === k ? 'nv-btn-primary' : 'nv-btn-ghost')}
                  onClick={() => { setAltaAni(altaAni === k ? null : k); setLineErr(''); setLineCred(null); }}>
                  + {label}
                </button>
              ))}
            </div>
            {altaAni === 'registro' && (
            <div style={{ marginTop:14 }}>
            <div className="nv-form-row" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)' }}>
              <div className="nv-form-field">
                <label className="nv-label">Plan (obligatorio)</label>
                <select className="nv-select" value={lineForm.plan_id}
                  onChange={e => setLineForm(f => ({ ...f, plan_id: e.target.value }))}>
                  <option value="">Seleccionar plan...</option>
                  {plansList.map(p => <option key={p.id} value={p.id}>{p.name || p.nombre}</option>)}
                </select>
              </div>
              <div className="nv-form-field">
                <label className="nv-label">ANI (opcional — automático si vacío)</label>
                <input className="nv-input" placeholder="automático del rango"
                  value={lineForm.did_number}
                  onChange={e => setLineForm(f => ({ ...f, did_number: e.target.value.replace(/\D/g,'') }))}
                  style={{ fontFamily:'var(--font-mono)' }} />
              </div>
            </div>
            {lineErr && (
              <div style={{ padding:'6px 10px', background:'var(--danger-bg)', color:'var(--danger)',
                borderRadius:'var(--r-sm)', fontSize:11, marginBottom:10 }}>{lineErr}</div>
            )}
            <button className="nv-btn nv-btn-primary" onClick={handleAddLine} disabled={lineBusy || !lineForm.plan_id}>
              {lineBusy ? 'Creando...' : 'Crear ANI con registro'}
            </button>
            </div>
            )}
            {lineCred && (
              <div style={{ marginTop:12, padding:'10px 12px', background:'var(--success-bg)', borderRadius:'var(--r-sm)' }}>
                <div style={{ fontWeight:700, color:'var(--success)', marginBottom:4 }}>
                  ✓ Línea creada — credenciales SIP (se muestran una sola vez):
                </div>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:12, color:'var(--text-primary)' }}>
                  Usuario / DID: {lineCred.sip_user}<br/>Contraseña: {lineCred.sip_password}
                </div>
              </div>
            )}
          </div>

          {lines.length > 0 && (
          <div className="nv-card" style={{ marginBottom:12 }}>
            <div className="nv-card-header">
              <span className="nv-card-title">ANIs con registro</span>
              <span style={{ fontFamily:'var(--font-mono)', fontSize:11, color:'var(--text-muted)' }}>
                {lines.length} {lines.length === 1 ? 'línea' : 'líneas'}
              </span>
            </div>
            {lines.length > 0 ? (
              <div className="nv-table-wrap">
                <table className="nv-table">
                  <thead><tr>
                    <th>DID / ANI</th><th>Alias</th><th>Plan</th><th>Estado</th><th>SIP</th><th>Alta</th><th></th>
                  </tr></thead>
                  <tbody>
                    {lines.map((l) => (
                      <Fragment key={l.id}>
                      <tr>
                        <td style={{ fontFamily:'var(--font-mono)', fontWeight:700, fontSize:13, color:'var(--brand)' }}>
                          {l.did_number}
                        </td>
                        {/* Alias = forma nacional del DID (columna generada did_nsn). Su PRIMER
                            dígito es el código de área que el ruteo usa para completar un local
                            de 7 dígitos, así que un alias que no mida 8 no es cosmético: el
                            dialplan no puede derivar el área y cae a su default. */}
                        <td style={{ fontFamily:'var(--font-mono)', fontSize:12 }}>
                          {l.alias || '—'}
                          {l.area ? (
                            <span style={{ fontSize:9, marginLeft:6, padding:'1px 5px', borderRadius:3,
                              background:'var(--bg-raised)', color:'var(--text-muted)', fontWeight:600 }}
                              title={`Código de área ${l.area}, deducido del primer dígito del alias`}>
                              área {l.area}
                            </span>
                          ) : (
                            <span style={{ fontSize:9, marginLeft:6, padding:'1px 5px', borderRadius:3,
                              background:'var(--warning-bg)', color:'var(--warning)', fontWeight:600 }}
                              title="El alias no mide 8 dígitos: el DID no está canonizado y el ruteo no puede deducir el área de esta línea (usa su default)">
                              sin área
                            </span>
                          )}
                        </td>
                        <td>{l.plan_nombre || '—'}</td>
                        <td>
                          <span style={{ fontSize:10, padding:'2px 6px', borderRadius:3, fontWeight:600,
                            background: l.status==='active' ? 'var(--success-bg)' : l.status==='suspended' ? 'var(--warning-bg)' : 'var(--info-bg)',
                            color: l.status==='active' ? 'var(--success)' : l.status==='suspended' ? 'var(--warning)' : 'var(--info)' }}>
                            {l.status}
                          </span>
                        </td>
                        {/* Estado SIP del registro. Solo dice algo en una línea ACTIVA con
                            registro: en pending/suspended, o en una troncal (IP-trust, no hay
                            REGISTER), el backend manda `registro: null` y acá va '----'.
                            El hover trae la IP real del registro y el delay del qualify. */}
                        <td style={{ fontSize:11 }}>
                          {!l.registro ? (
                            <span style={{ color:'var(--text-muted)' }}>----</span>
                          ) : l.registro.registrado ? (
                            <span
                              title={`${l.registro.ip || 'IP no disponible'}${
                                l.registro.rtt_ms != null ? ` (${l.registro.rtt_ms} ms)` : ' (delay n/d)'}`}
                              style={{ color:'var(--success)', fontWeight:600, cursor:'help',
                                borderBottom:'1px dotted var(--success)' }}>
                              Registrado
                            </span>
                          ) : (
                            <span style={{ color:'var(--warning)', fontWeight:600 }}
                              title="La línea está activa pero el equipo no tiene un registro SIP vigente">
                              No registrado
                            </span>
                          )}
                        </td>
                        <td style={{ fontSize:10, color:'var(--text-muted)' }}>{fmtDate(l.fecha_activacion)}</td>
                        <td style={{ textAlign:'right', whiteSpace:'nowrap' }}>
                          {CAPS.showTecnico && (
                            <button className="nv-btn nv-btn-sm" onClick={() => toggleLineTech(l.id)}
                              style={{ marginRight:6 }} title="Ver información técnica de la línea">
                              {techOpen[l.id] ? '▲ Técnico' : '🔧 Técnico'}
                            </button>
                          )}
                          {CAPS.manageLineas && (
                            <button className="nv-btn nv-btn-sm" onClick={() => openDidChange(l)}
                              style={{ marginRight:6 }}>
                              ✎ Cambiar DID
                            </button>
                          )}
                          {CAPS.showQr && (
                            <button className="nv-btn nv-btn-sm" onClick={() => setQrLine(l)}
                              style={{ marginRight:6 }} title="QR para registrar la línea en el Softphone">
                              ▦ Generar QR
                            </button>
                          )}
                          <button className="nv-btn nv-btn-sm" onClick={() => setPermLine(l)}
                            style={{ marginRight:6 }}
                            title="Permisos de marcado de ESTA línea (la pestaña Permisos configura todas)">
                            🔒 Permisos
                          </button>
                          <button className="nv-btn nv-btn-sm" onClick={() => openDidHistory(l)}
                            style={{ marginRight:6 }}>
                            🕘 Historial
                          </button>
                          {CAPS.manageLineas && (
                            <button className="nv-btn nv-btn-danger nv-btn-sm" onClick={() => handleRemoveLine(l.id, l.did_number)}>
                              ✕ Quitar
                            </button>
                          )}
                        </td>
                      </tr>
                      {CAPS.showTecnico && techOpen[l.id] && (
                        <tr>
                          {/* 7 columnas (Alias + SIP): si no se actualiza, la fila técnica
                              deja una celda huérfana al final de la tabla. */}
                          <td colSpan={7} style={{ background:'var(--bg-subtle, var(--bg-hover))', padding:'14px 16px' }}>
                            {techBusy[l.id] ? (
                              <div style={{ fontSize:12, color:'var(--text-muted)', textAlign:'center', padding:'8px 0' }}>
                                Cargando información técnica…
                              </div>
                            ) : techErr[l.id] ? (
                              <div style={{ fontSize:12, color:'var(--danger)', textAlign:'center', padding:'8px 0' }}>
                                {techErr[l.id]}
                              </div>
                            ) : techData[l.id] ? (
                              <div>
                                <div style={{ display:'flex', gap:6, alignItems:'center', marginBottom:10, flexWrap:'wrap' }}>
                                  <span style={{ fontSize:11, fontWeight:700, color:'var(--text-primary)', marginRight:4 }}>
                                    Información técnica — línea {l.did_number}
                                  </span>
                                  {techData[l.id].is_registered
                                    ? <span style={{ fontSize:10, color:'#15803d', background:'#dcfce7', padding:'2px 8px', borderRadius:4, fontWeight:600 }}>● Registrado</span>
                                    : <span style={{ fontSize:10, color:'#b45309', background:'#fef3c7', padding:'2px 8px', borderRadius:4, fontWeight:600 }}>○ No registra</span>}
                                  {techData[l.id].sbc && <span style={{ fontSize:10, color:'var(--info)', background:'var(--info-bg)', padding:'2px 8px', borderRadius:4, fontWeight:700, letterSpacing:'.04em' }}>SBC</span>}
                                </div>
                                {renderLineTech(techData[l.id], l.id)}
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ textAlign:'center', padding:'24px 0', color:'var(--text-muted)', fontSize:12 }}>
                Sin líneas aún — agregá la primera abajo.
              </div>
            )}
          </div>
          )}

          <TroncalCliente cliente={client} editable={CAPS.manageTroncal}
                          permisosEditables={CAPS.editPermisos}
                          altaModo={altaAni === 'uno' || altaAni === 'pool' ? altaAni : null}
                          onAltaCerrada={() => setAltaAni(null)}
                          onCargadas={setNTroncales} />
        </div>
      );

      case 'Contrato': return (
        <div>
        <div className="nv-card" style={{ marginBottom:12 }}>
          <div className="nv-card-header"><span className="nv-card-title">Contrato</span></div>

          {/* Número de contrato — solo lectura (la edición del nº se retiró de la ficha) */}
          <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 0', marginBottom:12, borderBottom:'1px solid var(--border)', flexWrap:'wrap' }}>
            <span style={{ fontSize:12, color:'var(--text-muted)', minWidth:150 }}>Número de contrato</span>
            <span style={{ fontWeight:600, color:'var(--text-primary)' }}>{client?.numero_contrato || '—'}</span>
          </div>

          {contract ? (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 20px' }}>
              {[
                ['Estado contrato', <span className={`nv-badge ${contract.signed?'nv-badge-ok':'nv-badge-warn'}`}>{contract.signed?'Firmado':'Pendiente'}</span>],
                ['Fecha firma',     fmtDT(contract.signed_at)],
                ['Firmado vía',     contract.signed_via || '—'],
                ['Hash documento',  contract.doc_hash ? contract.doc_hash.slice(0,16)+'...' : '—'],
                ['IP firma',        contract.ip_address || '—'],
                ['Versión doc',     contract.doc_version || '—'],
              ].map(([lbl,val]) => (
                <div key={lbl} style={{ padding:'6px 0', borderBottom:'1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize:9, textTransform:'uppercase', letterSpacing:'.07em', color:'var(--text-muted)', marginBottom:2 }}>{lbl}</div>
                  <div style={{ fontSize:12, color:'var(--text-primary)', fontWeight:500 }}>{val}</div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign:'center', padding:'32px 0', color:'var(--text-muted)', fontSize:12 }}>Sin contrato registrado</div>
          )}
        </div>

        {/* Documentos firmados — servicio independiente (stack de contratos).
            Un fallo suyo (docsCaido) no debe impedir ver el resto de esta ficha. */}
        <div className="nv-card">
          <div className="nv-card-header"><span className="nv-card-title">Documentos firmados</span></div>

          {/* Acciones de edición del contrato — solo en la superficie de gestión (clientes).
              En el panel la pestaña Contrato ni siquiera existe. */}
          {SURFACE === 'clientes' && (
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:12 }}>
              <button className="nv-btn nv-btn-sm nv-btn-secondary" onClick={handleRegenerar} disabled={contratoBusy}>
                Regenerar contrato
              </button>
              <label className="nv-btn nv-btn-sm nv-btn-secondary"
                style={{ cursor: contratoBusy ? 'not-allowed' : 'pointer', opacity: contratoBusy ? 0.4 : 1 }}>
                Subir copia firmada
                <input type="file" accept="application/pdf" style={{ display:'none' }}
                  disabled={contratoBusy} onChange={handleSubirFirmada} />
              </label>
              <button className="nv-btn nv-btn-sm nv-btn-secondary" onClick={openTerminos} disabled={contratoBusy}>
                Editar términos
              </button>
            </div>
          )}

          {SURFACE === 'clientes' && contratoAviso && (
            <div className={`nv-alert nv-alert-${contratoAviso.kind === 'err' ? 'err' : contratoAviso.kind === 'ok' ? 'ok' : 'warn'}`}
              style={{ marginBottom:12 }}>
              {contratoBusy && contratoAviso.kind === 'info' && <span className="nv-spinner" />}
              <span>{contratoAviso.text}</span>
            </div>
          )}

          {docsCaido ? (
            <div className="nv-alert-warn" style={{ padding:'8px 10px', fontSize:11, lineHeight:1.5, borderRadius:'var(--r-sm)', color:'var(--warning)' }}>
              Servicio de contratos no disponible. El resto de la ficha funciona con normalidad.
            </div>
          ) : docs.length === 0 ? (
            <div style={{ fontSize:12, color:'var(--text-muted)', padding:'8px 0' }}>Sin documentos archivados.</div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr style={{ color:'var(--text-muted)', fontSize:9, textTransform:'uppercase', letterSpacing:'.07em' }}>
                  <th style={{ textAlign:'left',  padding:'4px 0' }}>Archivo</th>
                  <th style={{ textAlign:'left',  padding:'4px 0' }}>Tipo</th>
                  <th style={{ textAlign:'left',  padding:'4px 0' }}>Estado</th>
                  <th style={{ textAlign:'left',  padding:'4px 0 4px 12px' }}>Firma</th>
                  <th style={{ textAlign:'right', padding:'4px 0 4px 12px' }}></th>
                </tr>
              </thead>
              <tbody>
                {docs.map(d => (
                  <tr key={d.id} style={{ borderTop:'1px solid var(--border-subtle)' }}>
                    <td style={{ padding:'6px 0' }}>{d.archivo_nombre || '—'}</td>
                    <td style={{ padding:'6px 0', color:'var(--text-secondary)' }}>{d.tipo || '—'}</td>
                    <td style={{ padding:'6px 0' }}><span className="nv-badge nv-badge-muted">{d.estado || '—'}</span></td>
                    <td style={{ padding:'6px 0 6px 12px' }}><FirmaBadge d={d} /></td>
                    <td style={{ padding:'6px 0 6px 12px', textAlign:'right' }}>
                      <button className="nv-btn nv-btn-sm" onClick={() => setVerDoc(d.id)}>Ver</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        </div>
      );

      case 'Pagos': return (
        <div className="nv-card">
          <div className="nv-card-header"><span className="nv-card-title">Historial de pagos</span></div>
          {(payment?.payments?.length > 0) ? (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {payment.payments.map((p) => (
                <div key={p.id} style={{ border:'1px solid var(--border-subtle)', borderRadius:8, padding:'10px 14px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                    <span style={{ fontSize:14, fontWeight:700, fontFamily:'var(--font-mono)', color:'var(--text-primary)' }}>
                      {p.amount != null ? `$${parseFloat(p.amount).toFixed(2)} ${p.currency || ''}`.trim() : '—'}
                    </span>
                    <span className={`nv-badge ${p.status==='approved'?'nv-badge-ok':p.status==='pending'?'nv-badge-warn':''}`}>{p.status || '—'}</span>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px 20px', fontSize:11, color:'var(--text-secondary)' }}>
                    <div>Gateway: <b style={{ color:'var(--text-primary)' }}>{p.gateway || '—'}</b></div>
                    <div>Ref: <b style={{ color:'var(--text-primary)' }}>{p.gateway_ref || '—'}</b></div>
                    <div>Concepto: <b style={{ color:'var(--text-primary)' }}>{p.concept || '—'}</b></div>
                    <div>Fecha: <b style={{ color:'var(--text-primary)' }}>{fmtDT(p.paid_at || p.created_at)}</b></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign:'center', padding:'32px 0', color:'var(--text-muted)', fontSize:12 }}>Sin pagos registrados</div>
          )}
        </div>
      );

      case 'Historial': return (
        <div className="nv-card">
          <div className="nv-card-header"><span className="nv-card-title">Historial de actividad</span></div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {[
              { icon:'◎', label:'Cuenta creada',         date: data?.created_at,        ok: true  },
              { icon:'✉', label:'Email verificado',       date: data?.email_verified_at, ok: steps?.email_verified },
              { icon:'▣', label:'Plan seleccionado',      date: null,                    ok: steps?.plan_selected  },
              { icon:'◈', label:'Contrato firmado (OTP)', date: contract?.signed_at,     ok: contract?.signed      },
              { icon:'◉', label:'Pago procesado',         date: payment?.paid_at,        ok: payment?.paid         },
              { icon:'⊕', label:'SIP activado',           date: null,                    ok: steps?.sip_activated  },
              { icon:'☎', label:'DID asignado',           date: dids?.dids?.[0]?.fecha_asignacion, ok: dids?.dids?.length > 0 },
            ].map(({ icon, label, date, ok }) => (
              <div key={label} style={{
                display:'flex', alignItems:'center', gap:10,
                padding:'8px 10px',
                background: ok ? 'var(--success-bg)' : 'var(--bg-raised)',
                borderRadius:'var(--r-sm)',
                border:`1px solid ${ok ? 'rgba(0,201,141,0.15)' : 'var(--border)'}`,
                opacity: ok ? 1 : 0.5,
              }}>
                <span style={{ fontSize:14, color: ok ? 'var(--success)' : 'var(--text-muted)', width:18, textAlign:'center' }}>{icon}</span>
                <span style={{ flex:1, fontSize:12, color: ok ? 'var(--text-primary)' : 'var(--text-muted)' }}>{label}</span>
                {date && <span style={{ fontSize:10, color:'var(--text-muted)', fontFamily:'var(--font-mono)' }}>{fmtDT(date)}</span>}
                <span style={{ fontSize:10, fontWeight:700, color: ok ? 'var(--success)' : 'var(--text-muted)' }}>{ok ? '✓' : '○'}</span>
              </div>
            ))}
          </div>
        </div>
      );

      case 'Permisos':
        // Los permisos de marcado viven por ANI (`asterisk.ani_permissions`): es lo que el AGI
        // compara contra el caller-ID de la llamada. Un cliente NO tiene un ANI — tiene uno por
        // línea —, así que esta pestaña configura al cliente entero y lista cómo marca hoy cada
        // línea. Antes resolvía un solo ANI para todo el cliente y las demás líneas quedaban con
        // el default de cada categoría sin decirlo.
        //
        // ⚠️ Los clientes que SOLO tienen troncales no tienen esta pestaña: sus permisos se configuran por
        // troncal, desde el botón 🔒 Permisos de su fila en la pestaña Troncal. Ver TABS_VISIBLES.
        return <PermisosCliente clientId={id} editable={CAPS.editPermisos} />;

      default: return null;
    }
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', overflow:'hidden' }}>
      {dialogoConfirm}
      {/* Header del detalle */}
      <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:12, flexShrink:0 }}>
        <div style={{ width:38, height:38, borderRadius:'50%', background:'var(--brand-subtle)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:700, color:'var(--brand)', flexShrink:0 }}>
          {(client?.name||data?.name||'?').slice(0,2).toUpperCase()}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:14, fontWeight:700, color:'var(--text-primary)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
            {client?.name || data?.name || `Cliente #${id}`}
          </div>
          <div style={{ fontSize:11, color:'var(--text-muted)' }}>{client?.email || data?.email || '—'}</div>
        </div>
        <StatusBadge s={client?.status || data?.status} />
        <button className="nv-btn nv-btn-ghost nv-btn-sm" onClick={onClose}>✕</button>
      </div>

      {/* Tabs */}
      <div style={{ padding:'8px 14px 0', borderBottom:'1px solid var(--border)', display:'flex', gap:2, flexShrink:0, flexWrap:'wrap' }}>
        {tabsVisibles.map(t => (
          <button key={t}
            onClick={() => setTab(t)}
            style={{
              background: tab===t ? 'var(--bg-surface)' : 'transparent',
              border: tab===t ? '1px solid var(--border)' : '1px solid transparent',
              borderBottom: tab===t ? '1px solid var(--bg-surface)' : 'none',
              color: tab===t ? 'var(--brand)' : 'var(--text-secondary)',
              padding:'6px 12px', borderRadius:'var(--r-sm) var(--r-sm) 0 0',
              fontSize:11.5, fontWeight: tab===t ? 700 : 400,
              cursor:'pointer', fontFamily:'var(--font-ui)',
              transition:'all var(--t)',
            }}
          >{t === 'Líneas' ? 'Servicios' : t}</button>
        ))}
      </div>

      {/* Contenido tab */}
      <div style={{ flex:1, overflow:'auto', padding:14 }}>
        {renderTab()}
      </div>

      {/* Cambiar DID/ANI de una línea */}
      {didChange && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', display:'flex',
          alignItems:'center', justifyContent:'center', zIndex:1100 }}
          onClick={e => { if(e.target===e.currentTarget) setDidChange(null); }}>
          <div style={{ background:'var(--bg-surface)', borderRadius:'var(--r)', border:'1px solid var(--border)',
            padding:24, width:440, maxWidth:'90vw' }}>
            <div style={{ fontSize:15, fontWeight:700, color:'var(--text-primary)', marginBottom:4 }}>
              Cambiar DID / ANI
            </div>
            <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:14 }}>
              Línea actual: <code style={{ color:'var(--brand)' }}>{didChange.did_number}</code>
            </div>

            {!didChangeRes ? (
              <>
                <div style={{ padding:'8px 10px', marginBottom:14, fontSize:11, lineHeight:1.5,
                  background:'var(--warning-bg,#3a2a10)', color:'var(--warning,#d97706)',
                  borderRadius:'var(--r-sm)' }}>
                  El usuario SIP cambia junto con el DID (el ruteo entrante marca al endpoint por
                  su número). <strong>El equipo del cliente dejará de registrarse</strong> hasta
                  reconfigurarlo con el usuario nuevo.
                </div>
                <label className="nv-label">Nuevo DID</label>
                <input className="nv-input" value={didChangeVal} placeholder="Ej: 59343901992"
                  onChange={e => setDidChangeVal(e.target.value.replace(/\D/g,''))}
                  style={{ fontFamily:'var(--font-mono)', marginBottom:10 }} />
                <label className="nv-label">Motivo (opcional)</label>
                <input className="nv-input" value={didChangeMot} placeholder="Ej: pedido del cliente"
                  onChange={e => setDidChangeMot(e.target.value)} style={{ marginBottom:12 }} />
                {didChangeErr && (
                  <div style={{ padding:'6px 10px', background:'var(--danger-bg)', color:'var(--danger)',
                    borderRadius:'var(--r-sm)', fontSize:11, marginBottom:10 }}>{didChangeErr}</div>
                )}
                <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                  <button className="nv-btn nv-btn-sm" onClick={() => setDidChange(null)}>Cancelar</button>
                  <button className="nv-btn nv-btn-primary nv-btn-sm" onClick={handleChangeDid}
                    disabled={didChangeBusy || !didChangeVal.trim()}>
                    {didChangeBusy ? 'Cambiando...' : 'Cambiar DID'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ padding:'10px 12px', background:'var(--success-bg)', borderRadius:'var(--r-sm)', marginBottom:12 }}>
                  <div style={{ fontWeight:700, color:'var(--success)', marginBottom:4 }}>
                    ✓ {didChangeRes.did_anterior} → {didChangeRes.did_number}
                  </div>
                  <div style={{ fontFamily:'var(--font-mono)', fontSize:12, color:'var(--text-primary)' }}>
                    Usuario SIP: {didChangeRes.sip_user}<br/>
                    Contraseña: {didChangeRes.sip_password}
                  </div>
                  <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:6 }}>
                    {didChangeRes.password_rotada
                      ? 'La contraseña se rotó (la anterior no era recuperable). Se muestra una sola vez.'
                      : 'Se conservó la contraseña anterior.'}
                  </div>
                </div>
                <div style={{ display:'flex', justifyContent:'flex-end' }}>
                  <button className="nv-btn nv-btn-primary nv-btn-sm" onClick={() => setDidChange(null)}>Listo</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Permisos de marcado de UNA línea */}
      {permLine && (
        <PermisosLineaModal linea={permLine} editable={CAPS.editPermisos}
          onClose={() => setPermLine(null)}/>
      )}

      {/* QR de registro de UNA línea para el Softphone */}
      {qrLine && <QrLineaModal linea={qrLine} onClose={() => setQrLine(null)}/>}

      {/* Historial de DIDs de una línea */}
      {histLine && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', display:'flex',
          alignItems:'center', justifyContent:'center', zIndex:1100 }}
          onClick={e => { if(e.target===e.currentTarget) setHistLine(null); }}>
          <div style={{ background:'var(--bg-surface)', borderRadius:'var(--r)', border:'1px solid var(--border)',
            padding:24, width:660, maxWidth:'92vw', maxHeight:'84vh', overflowY:'auto' }}>
            <div style={{ fontSize:15, fontWeight:700, color:'var(--text-primary)', marginBottom:4 }}>
              Historial de DID / ANI
            </div>
            <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:16 }}>
              Línea <code style={{ color:'var(--brand)' }}>{histLine.did_number}</code>
            </div>

            {/* Log de servicio del ANI: qué le pasó a la línea, contado en castellano.
                El texto lo arma el backend (app/ani_narrativa.py) para que esta ficha y la
                pantalla /historial-ani digan lo mismo. */}
            <div style={{ fontSize:9, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase',
              letterSpacing:'.07em', marginBottom:8 }}>
              Eventos del servicio · últimos 90 días
            </div>
            {/* Estado del registro: lo primero que se mira ante un reclamo de
                intermitencia. `registro` viene null para troncales IP, donde "perder el
                registro" no significa nada. */}
            {histReg && (
              <div style={{ display:'flex', alignItems:'baseline', gap:8, marginBottom:12,
                padding:'8px 10px', borderRadius:6, background:'var(--bg-subtle, rgba(0,0,0,.03))',
                borderLeft:`3px solid ${histReg.registrado ? '#1e8e3e' : '#c5221f'}` }}>
                <span style={{ fontSize:12, fontWeight:700,
                  color: histReg.registrado ? '#1e8e3e' : '#c5221f' }}>
                  {histReg.registrado ? 'Registrado' : 'Sin registro'}
                </span>
                <span style={{ fontSize:11, color:'var(--text-muted)' }}>
                  {histReg.desde && `desde ${fmtDT(histReg.desde)}`}
                  {histReg.caidas > 0
                    ? ` · ${histReg.caidas} ${histReg.caidas === 1 ? 'caída' : 'caídas'} en ${histReg.dias} días${histReg.sin_registro ? `, ${histReg.sin_registro} sin servicio` : ''}`
                    : ` · sin caídas en ${histReg.dias} días`}
                </span>
              </div>
            )}

            {histEvErr ? (
              <div style={{ fontSize:12, color:'var(--text-muted)', padding:'8px 0' }}>{histEvErr}</div>
            ) : histEv.length === 0 ? (
              <div style={{ fontSize:12, color:'var(--text-muted)', padding:'8px 0' }}>
                Sin eventos. Si la línea nunca perdió el registro, nunca fue bloqueada y no tuvo
                llamadas fallidas, esto es lo esperado.
              </div>
            ) : (
              <div style={{ maxHeight:300, overflowY:'auto', paddingRight:4 }}>
                {histEv.map((e, i) => (
                  <div key={`${e.ts}-${i}`} style={{ borderLeft:`3px solid ${colorEvento(e.tipo)}`,
                    padding:'5px 0 5px 10px', marginBottom:7 }}>
                    <div style={{ fontSize:12, color:'var(--text-primary)', lineHeight:1.45 }}>
                      {e.mensaje || e.etiqueta}
                    </div>
                    <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:2 }}>
                      {fmtDT(e.ts)}
                      {e.q850 != null && ` · Q.850 ${e.q850}`}
                      {e.ip_publica && ` · ${e.ip_publica}`}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ fontSize:9, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase',
              letterSpacing:'.07em', margin:'20px 0 8px' }}>
              Cambios de número
            </div>
            {hist.length === 0 ? (
              <div style={{ fontSize:12, color:'var(--text-muted)', padding:'12px 0' }}>Sin movimientos registrados.</div>
            ) : (
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr style={{ color:'var(--text-muted)', fontSize:9, textTransform:'uppercase', letterSpacing:'.07em' }}>
                    <th style={{ textAlign:'left', padding:'4px 0' }}>Cambio</th>
                    <th style={{ textAlign:'left', padding:'4px 0' }}>Motivo</th>
                    <th style={{ textAlign:'left', padding:'4px 0' }}>Por</th>
                    <th style={{ textAlign:'left', padding:'4px 0' }}>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {hist.map(h => (
                    <tr key={h.id} style={{ borderTop:'1px solid var(--border-subtle)' }}>
                      <td style={{ padding:'6px 0', fontFamily:'var(--font-mono)' }}>
                        {h.did_anterior ? `${h.did_anterior} → ${h.did_nuevo}` : h.did_nuevo}
                      </td>
                      <td style={{ padding:'6px 0', color:'var(--text-secondary)' }}>{h.motivo || '—'}</td>
                      <td style={{ padding:'6px 0', color:'var(--text-muted)' }}>{h.changed_by || '—'}</td>
                      <td style={{ padding:'6px 0', color:'var(--text-muted)', fontSize:10 }}>{fmtDate(h.changed_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div style={{ display:'flex', justifyContent:'flex-end', marginTop:14 }}>
              <button className="nv-btn nv-btn-sm" onClick={() => setHistLine(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* Ver documento archivado (servicio de contratos) */}
      {verDoc && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', display:'flex',
          alignItems:'center', justifyContent:'center', zIndex:1100 }}
          onClick={e => { if(e.target===e.currentTarget) setVerDoc(null); }}>
          <div style={{ background:'var(--bg-surface)', borderRadius:'var(--r)', border:'1px solid var(--border)',
            padding:24, width:800, maxWidth:'92vw', maxHeight:'90vh', display:'flex', flexDirection:'column' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
              <span style={{ fontSize:15, fontWeight:700, color:'var(--text-primary)' }}>Documento</span>
              <button className="nv-btn nv-btn-sm" onClick={() => setVerDoc(null)}>Cerrar</button>
            </div>
            <VisorContrato documentoId={verDoc} />
          </div>
        </div>
      )}

      {/* Editar términos del contrato (solo superficie clientes). Al guardar, el backend
          regenera el contrato en BACKGROUND. */}
      {showTerminos && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', display:'flex',
          alignItems:'center', justifyContent:'center', zIndex:1100 }}
          onClick={e => { if(e.target===e.currentTarget && !terminosBusy) setShowTerminos(false); }}>
          <div style={{ background:'var(--bg-surface)', borderRadius:'var(--r)', border:'1px solid var(--border)',
            padding:24, width:560, maxWidth:'92vw', maxHeight:'88vh', overflowY:'auto' }}>
            <div style={{ fontSize:15, fontWeight:700, color:'var(--text-primary)', marginBottom:4 }}>
              Editar términos del contrato
            </div>
            <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:14 }}>
              Al guardar, el contrato se regenera automáticamente con estos términos.
            </div>

            {!terminosForm && terminosErr ? (
              // Falla de carga: NO se muestran los estándar (evita pisar términos reales al guardar).
              <>
                <div style={{ padding:'8px 10px', background:'var(--danger-bg)', color:'var(--danger)',
                  borderRadius:'var(--r-sm)', fontSize:12 }}>{terminosErr}</div>
                <div style={{ display:'flex', justifyContent:'flex-end', marginTop:16 }}>
                  <button className="nv-btn nv-btn-sm" onClick={() => setShowTerminos(false)}>Cerrar</button>
                </div>
              </>
            ) : !terminosForm ? (
              <div className="nv-loading" style={{ padding:'24px 0' }}>
                <span className="nv-spinner"/><span>Cargando términos…</span>
              </div>
            ) : (
              <>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px 16px' }}>
                  <div>
                    <label style={FORM_LABEL}>Duración (meses)</label>
                    <input type="number" min="0" style={FORM_INPUT} value={terminosForm.duracion_meses ?? ''}
                      onChange={e => setTerminosForm(f => ({ ...f, duracion_meses: e.target.value }))} />
                  </div>
                  <div>
                    <label style={FORM_LABEL}>Renovación automática</label>
                    <select style={FORM_INPUT} value={terminosForm.renovacion_automatica || 'no'}
                      onChange={e => setTerminosForm(f => ({ ...f, renovacion_automatica: e.target.value }))}>
                      <option value="yes">Sí</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  <div>
                    <label style={FORM_LABEL}>Permanencia mínima</label>
                    <select style={FORM_INPUT} value={terminosForm.permanencia_minima || 'no'}
                      onChange={e => setTerminosForm(f => ({ ...f, permanencia_minima: e.target.value }))}>
                      <option value="yes">Sí</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  <div>
                    <label style={FORM_LABEL}>Permanencia (meses)</label>
                    <input type="number" min="0" style={FORM_INPUT} value={terminosForm.permanencia_meses ?? ''}
                      onChange={e => setTerminosForm(f => ({ ...f, permanencia_meses: e.target.value }))} />
                  </div>
                  <div>
                    <label style={FORM_LABEL}>Derecho de inscripción (USD)</label>
                    <input type="number" min="0" step="0.01" style={FORM_INPUT} value={terminosForm.derecho_inscripcion ?? ''}
                      onChange={e => setTerminosForm(f => ({ ...f, derecho_inscripcion: e.target.value }))} />
                  </div>
                  <div>
                    <label style={FORM_LABEL}>Arbitraje</label>
                    <select style={FORM_INPUT} value={terminosForm.arbitraje || 'no'}
                      onChange={e => setTerminosForm(f => ({ ...f, arbitraje: e.target.value }))}>
                      <option value="yes">Sí</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  <div>
                    <label style={FORM_LABEL}>Autoriza llamadas automáticas</label>
                    <select style={FORM_INPUT} value={terminosForm.autoriza_llamadas_automaticas || 'no'}
                      onChange={e => setTerminosForm(f => ({ ...f, autoriza_llamadas_automaticas: e.target.value }))}>
                      <option value="yes">Sí</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  <div>
                    <label style={FORM_LABEL}>Autoriza guía telefónica</label>
                    <select style={FORM_INPUT} value={terminosForm.autoriza_guia_telefonica || 'no'}
                      onChange={e => setTerminosForm(f => ({ ...f, autoriza_guia_telefonica: e.target.value }))}>
                      <option value="yes">Sí</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                </div>

                {terminosErr && (
                  <div style={{ padding:'6px 10px', background:'var(--danger-bg)', color:'var(--danger)',
                    borderRadius:'var(--r-sm)', fontSize:11, marginTop:12 }}>{terminosErr}</div>
                )}

                <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:16 }}>
                  <button className="nv-btn nv-btn-sm" onClick={() => setShowTerminos(false)} disabled={terminosBusy}>Cancelar</button>
                  <button className="nv-btn nv-btn-primary nv-btn-sm" onClick={saveTerminos} disabled={terminosBusy}>
                    {terminosBusy ? 'Guardando…' : 'Guardar términos'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// clients.origin -> empresa visible. 'eneural' = alta desde el panel, que cuenta como
// Netvoice (decisión de Carlos, 2026-10-21; mismo criterio que auth.origins_permitidos).
function empresaDeOrigin(origin) {
  if (!origin) return '—';
  return origin === 'eneural' ? 'NETVOICE' : origin.toUpperCase();
}

const ALTA_VACIA = { tipo_cliente:'', primer_nombre:'', segundo_nombre:'', primer_apellido:'', segundo_apellido:'', razon_social:'', ruc:'', email:'', phone:'', provincia:'', ciudad:'', direccion:'', modo_pago:'caja', banco_codigo:'', tipo_cuenta:'AHO', numero_cuenta:'', empresa:'' };

// Empresas que el usuario puede asignar en el alta: las suyas si está acotado (users.empresas),
// si no las tres. El backend valida lo mismo (onboarding agent/register); esto es solo UI.
const EMPRESAS_CLIENTE = [
  { key:'netvoice', label:'Netvoice' },
  { key:'netlife',  label:'Netlife'  },
  { key:'telconet', label:'Telconet' },
];
function empresasAsignables(user) {
  const propias = (user?.empresas || '').split(',').filter(Boolean);
  return propias.length ? EMPRESAS_CLIENTE.filter(e => propias.includes(e.key)) : EMPRESAS_CLIENTE;
}

/* ── Componente principal ── */
export default function ClientesV2() {
  const CAPS = useCaps();
  const { user: usuarioSesion } = useAuth();
  const empresasAlta = empresasAsignables(usuarioSesion);
  const [clientes,    setClientes]    = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [search,      setSearch]      = useState('');
  const [selected,    setSelected]    = useState(null);
  const [filter,      setFilter]      = useState('all');
  const [sipFilter,   setSipFilter]   = useState('all');  // 'all'|'registered'|'unregistered'|'none'
  const [showCreate,  setShowCreate]  = useState(false);
  const [creating,    setCreating]    = useState(false);
  const [createErr,   setCreateErr]   = useState('');
  const [createForm,  setCreateForm]  = useState({ ...ALTA_VACIA });
  const [bancos,      setBancos]      = useState([]);
  const [tiposCliente,setTiposCliente]= useState(TIPOS_CLIENTE);
  const [editTarget,  setEditTarget]  = useState(null);
  const [editForm,    setEditForm]    = useState({});
  const [editErr,     setEditErr]     = useState('');
  const [editBusy,    setEditBusy]    = useState(false);
  const [deleteTarget,setDeleteTarget]= useState(null);
  const [deleteInput, setDeleteInput] = useState('');
  const [deleteBusy,  setDeleteBusy]  = useState(false);
  const [actionBusy,  setActionBusy]  = useState('');
  const [page,        setPage]        = useState(1);
  const [totalPages,  setTotalPages]  = useState(1);
  const [totalCount,  setTotalCount]  = useState(0);
  const [counts,      setCounts]      = useState({ all:0, active:0, pending:0, suspended:0 });
  const [sipCounts,   setSipCounts]   = useState({ all:0, registered:0, unregistered:0, none:0 });
  const PAGE_SIZE = 50;

  const load = useCallback(async (q = '', pg = 1, st = 'all', ss = 'all') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, page: pg });
      if (q.trim()) params.set('search', q.trim());
      if (st && st !== 'all') params.set('status', st);
      if (ss && ss !== 'all') params.set('sip_state', ss);
      const res = await apiv1.get(`/onboarding/agent/list?${params}`);
      setClientes(res.data.data || []);
      setTotalPages(res.data.pages || 1);
      setTotalCount(res.data.total || 0);
      if (res.data.counts)     setCounts(res.data.counts);
      if (res.data.sip_counts) setSipCounts(res.data.sip_counts);
    } catch { setClientes([]); }
    finally { setLoading(false); }
  }, [PAGE_SIZE]);

  // Carga inicial
  useEffect(() => { load('', 1, 'all', 'all'); }, [load]);

  // Catálogo de bancos (para el mandato de débito bancario) — una vez al montar
  useEffect(() => {
    (async () => {
      const r = await safe(() => apiv1.get('/onboarding/bancos'));
      setBancos(r?.data?.bancos || []);
    })();
  }, []);

  // El catálogo real vive en el backend (app/segmento.py); TIPOS_CLIENTE es solo el
  // fallback para que el alta siga usable si la llamada falla.
  useEffect(() => {
    (async () => {
      const r = await safe(() => apiv1.get('/onboarding/tipos-cliente'));
      if (r?.data?.data?.length) setTiposCliente(r.data.data);
    })();
  }, []);

  // Búsqueda + filtros (estado del cliente + estado de troncal) con debounce
  // 400ms — resetea a página 1. Todos los filtros se aplican en el SERVIDOR.
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(search, 1, filter, sipFilter); }, 400);
    return () => clearTimeout(t);
  }, [search, filter, sipFilter, load]);

  // Cambio de página
  useEffect(() => { load(search, page, filter, sipFilter); }, [page]);

  // El filtrado por estado y búsqueda ya viene resuelto del servidor.
  const filtered = clientes;

  const openEdit = (e, c) => {
    e.stopPropagation();
    // El nombre NO se edita acá: vive en partes (1er/2do nombre, apellidos, razón social)
    // y se edita en la ficha. Editarlo suelto dejaría `name` desincronizado de sus partes.
    setEditForm({ ruc: c.ruc||'', phone: c.phone||'', tipo_cliente: c.tipo_cliente||'',
                  provincia: c.provincia||'', ciudad: c.ciudad||'', direccion: c.direccion||'' });
    setEditErr('');
    setEditTarget(c);
  };

  const handleSave = async () => {
    setEditBusy(true); setEditErr('');
    try {
      // El tipo tiene endpoint propio (valida contra el catálogo). Es solo descriptivo: no
      // restringe qué servicios tiene el cliente. Va primero; si rechaza no se guarda nada más.
      const { tipo_cliente, ...resto } = editForm;
      if (tipo_cliente && tipo_cliente !== (editTarget.tipo_cliente || ''))
        await apiv1.patch(`/onboarding/client/${editTarget.id}/tipo`, { tipo_cliente });
      await apiv1.patch(`/onboarding/client/${editTarget.id}`, resto);
      setEditTarget(null);
      load(search);
    } catch(e) { setEditErr(e.response?.data?.detail || 'Error al guardar'); }
    finally { setEditBusy(false); }
  };

  const handleToggleStatus = async (e, c) => {
    e.stopPropagation();
    if (actionBusy) return;
    setActionBusy(c.id);
    try {
      await apiv1.patch(`/onboarding/client/${c.id}/status`);
      load(search);
    } catch {}
    finally { setActionBusy(''); }
  };

  const openDelete = (e, c) => {
    e.stopPropagation();
    setDeleteInput('');
    setDeleteTarget(c);
  };

  const handleDelete = async () => {
    if (deleteInput.trim().toLowerCase() !== deleteTarget.name.trim().toLowerCase()) return;
    setDeleteBusy(true);
    try {
      await apiv1.delete(`/onboarding/client/${deleteTarget.id}`);
      setDeleteTarget(null);
      if (selected === deleteTarget.id) setSelected(null);
      load(search);
    } catch(e) { alert(e.response?.data?.detail || 'Error al eliminar'); }
    finally { setDeleteBusy(false); }
  };

  const handleCreate = async () => {
    // Persona natural: nombre + apellido. Empresa: basta la razón social.
    const esEmpresa = !!createForm.razon_social.trim();
    if (!esEmpresa && !(createForm.primer_nombre.trim() && createForm.primer_apellido.trim()))
      return setCreateErr('Indica 1er nombre y 1er apellido, o la razón social si es empresa');
    if (!createForm.tipo_cliente) return setCreateErr('Elegí el tipo de cliente: define si le corresponde una línea o una troncal');
    if (!createForm.email.trim()) return setCreateErr('El email es obligatorio');
    if (!createForm.provincia) return setCreateErr('La provincia es obligatoria');
    setCreating(true);
    setCreateErr('');
    try {
      const payload = {
        tipo_cliente:     createForm.tipo_cliente,
        primer_nombre:    createForm.primer_nombre.trim()    || null,
        segundo_nombre:   createForm.segundo_nombre.trim()   || null,
        primer_apellido:  createForm.primer_apellido.trim()  || null,
        segundo_apellido: createForm.segundo_apellido.trim() || null,
        razon_social:     createForm.razon_social.trim()     || null,
        ruc:       createForm.ruc.trim()       || null,
        email:     createForm.email.trim(),
        phone:     createForm.phone.trim()     || null,
        provincia: createForm.provincia        || null,
        ciudad:    createForm.ciudad.trim()    || null,
        direccion: createForm.direccion.trim() || null,
        modo_pago: createForm.modo_pago,
        empresa:   createForm.empresa || (empresasAlta.length === 1 ? empresasAlta[0].key : null),
      };
      // El backend ignora los campos de cuenta si no es débito bancario.
      if (createForm.modo_pago === 'debito_banco') {
        payload.banco_codigo  = createForm.banco_codigo || null;
        payload.tipo_cuenta   = createForm.tipo_cuenta;
        payload.numero_cuenta = createForm.numero_cuenta.trim() || null;
      }
      await apiv1.post('/onboarding/agent/register', payload);
      setShowCreate(false);
      setCreateForm({ ...ALTA_VACIA });
      load();
    } catch(e) {
      setCreateErr(e.response?.data?.detail || 'Error al crear cliente');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="nv-page-header">
        <div>
          <div className="nv-page-title">Gestión de Clientes</div>
          <div className="nv-page-sub">{totalCount.toLocaleString()} clientes registrados · Netvoice onboarding</div>
        </div>
        <div className="nv-page-actions">
          <button className="nv-btn nv-btn-secondary nv-btn-sm" onClick={() => load()} disabled={loading}>
            {loading ? <span className="nv-spinner" /> : '↺'} Actualizar
          </button>
          {CAPS.canCreate && (
            <button className="nv-btn nv-btn-primary nv-btn-sm" onClick={() => { setCreateErr(''); setShowCreate(true); }}>
              + Nuevo cliente
            </button>
          )}
        </div>
      </div>

      {/* Filtros rápidos — Estado del cliente */}
      <div style={{ display:'flex', gap:8, marginBottom:8, flexWrap:'wrap', alignItems:'center' }}>
        <span style={{ fontSize:10, color:'var(--text-muted)', marginRight:4 }}>Estado:</span>
        {[
          ['all','Todos',counts.all,'var(--text-muted)'],
          ['active','Activos',counts.active,'var(--success)'],
          ['pending','Pendientes',counts.pending,'var(--info)'],
          ['suspended','Suspendidos',counts.suspended,'var(--danger)'],
        ].map(([key,lbl,cnt,color]) => (
          <button key={key}
            onClick={() => setFilter(key)}
            className={`nv-btn nv-btn-sm ${filter===key ? 'nv-btn-secondary' : 'nv-btn-ghost'}`}
            style={{ borderColor: filter===key ? 'var(--border-active)' : undefined }}
          >
            {lbl}
            <span style={{ fontFamily:'var(--font-mono)', fontSize:10, color }}>{cnt}</span>
          </button>
        ))}
      </div>

      {/* Filtros — Registro SIP en vivo (asterisk.ps_contacts).
          Se llamaba "Troncal", con el sentido viejo de "la línea SIP del cliente". Ahora que
          "troncal" es un producto concreto, ese rótulo decía lo contrario de lo que hace:
          estos contadores EXCLUYEN a los clientes que SOLO tienen troncales (no hacen REGISTER, contarlos
          como "sin registro" los marcaba como problema). Por eso los números no suman el
          total de clientes. */}
      <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
        <span style={{ fontSize:10, color:'var(--text-muted)', marginRight:4 }}
          title="Solo clientes con línea: las troncales son IP-trust y no registran, así que no se cuentan acá">
          Registro SIP:
        </span>
        {[
          ['all','Todas',sipCounts.all,'var(--text-muted)'],
          ['registered','Registradas',sipCounts.registered,'var(--success)'],
          ['unregistered','Con línea s/registro',sipCounts.unregistered,'var(--warning)'],
          ['none','Sin línea',sipCounts.none,'var(--text-muted)'],
        ].map(([key,lbl,cnt,color]) => (
          <button key={key}
            onClick={() => setSipFilter(key)}
            className={`nv-btn nv-btn-sm ${sipFilter===key ? 'nv-btn-secondary' : 'nv-btn-ghost'}`}
            style={{ borderColor: sipFilter===key ? 'var(--border-active)' : undefined }}
          >
            {lbl}
            <span style={{ fontFamily:'var(--font-mono)', fontSize:10, color }}>{cnt}</span>
          </button>
        ))}
      </div>

      {/* Layout 2 columnas */}
      <div style={{ display:'grid', gridTemplateColumns: selected ? '340px 1fr' : '1fr', gap:14, minHeight:600 }}>

        {/* Lista */}
        <div className="nv-card" style={{ padding:0, display:'flex', flexDirection:'column', overflow:'hidden' }}>
          {/* Buscador */}
          <div style={{ padding:'10px 12px', borderBottom:'1px solid var(--border)' }}>
            <div className="nv-search">
              <span style={{ color:'var(--text-muted)', fontSize:12 }}>⌕</span>
              <input
                placeholder="Buscar por nombre, RUC o DID..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && <button onClick={() => setSearch('')} style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:13 }}>✕</button>}
            </div>
          </div>

          {/* Items */}
          <div style={{ flex:1, overflowY:'auto' }}>
            {loading && <div className="nv-loading"><span className="nv-spinner" /></div>}
            {!loading && filtered.length === 0 && (
              <div style={{ textAlign:'center', padding:'32px 0', color:'var(--text-muted)', fontSize:12 }}>Sin resultados</div>
            )}
            {filtered.map(c => {
              const isActive = selected === c.id;
              const [bg, color] = STATUS_COLORS[c.status] || STATUS_COLORS.inactive;
              return (
                <div key={c.id}
                  onClick={() => setSelected(isActive ? null : c.id)}
                  style={{
                    padding:'10px 14px',
                    borderBottom:'1px solid var(--border-subtle)',
                    cursor:'pointer',
                    background: isActive ? 'var(--brand-subtle)' : 'transparent',
                    borderLeft:`2px solid ${isActive ? 'var(--brand)' : 'transparent'}`,
                    transition:'all var(--t)',
                  }}
                >
                  <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:8 }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12.5, fontWeight:600, color: isActive ? 'var(--brand)' : 'var(--text-primary)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                        {c.name}
                      </div>
                      <div style={{ fontSize:10.5, color:'var(--text-muted)', marginTop:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                        {c.email}
                      </div>
                      <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:2 }}>
                        {empresaDeOrigin(c.origin)} · {fmtDate(c.created_at)}
                        {c.ruc ? <> · RUC {c.ruc}</> : ''}
                      </div>
                      {c.did_number && (
                        <div style={{ fontSize:10, color:'var(--info)', marginTop:2, fontFamily:'var(--font-mono)' }}>
                          ☎ {c.dids || c.did_number}
                          {c.lineas > 1 && (
                            <span style={{ color:'var(--text-muted)' }}> · {c.lineas} líneas</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div style={{ display:'flex', gap:4, flexShrink:0, alignItems:'center', flexWrap:'wrap', justifyContent:'flex-end', maxWidth:200 }}>
                      {/* 1. Estado del cliente */}
                      <span className="nv-badge" style={{ background:bg, color }}>
                        {c.status === 'active'    ? 'Activo'
                          : c.status === 'pending'   ? 'Pendiente'
                          : c.status === 'suspended' ? 'Suspendido'
                          : 'Inactivo'}
                      </span>
                      {/* 2. DID asignado — derivado de las líneas del contrato */}
                      <span title={c.lineas ? `${c.lineas} línea(s): ${c.dids}` : 'El cliente no tiene líneas'}
                        style={{ fontSize:9, padding:'2px 6px', borderRadius:3, fontWeight:600,
                                 background: c.did_number ? 'var(--success-bg)' : 'var(--danger-bg)',
                                 color:      c.did_number ? 'var(--success)'    : 'var(--danger)' }}>
                        {c.did_number ? 'Asignada' : 'No asignado'}
                      </span>
                      {/* 3. Estado en vivo. Una TRONCAL no hace REGISTER (es IP-trust), así que
                          decirle "No registra" era cierto pero engañoso: sonaba a problema
                          cuando es lo normal. Para esos clientes se muestra "Troncal", y el
                          color sale del qualify. `responde === null` = no se pudo verificar
                          (el puente al host no contestó) → ámbar, porque no saberlo NO es lo
                          mismo que no responder. */}
                      {c.servicio === 'troncal' ? (
                        <span title={
                          c.troncal?.nota ? c.troncal.nota
                            : c.troncal?.responde
                              ? `Responde el qualify${c.troncal.rtt_ms != null ? ` — ${c.troncal.rtt_ms} ms` : ''}`
                              : `No responde el qualify (${c.troncal?.estado || 'sin contacto'})`}
                          style={{ fontSize:9, padding:'2px 6px', borderRadius:3, fontWeight:600,
                                   background: c.troncal?.responde == null ? 'var(--warning-bg)'
                                     : c.troncal.responde ? 'var(--success-bg)' : 'var(--danger-bg)',
                                   color:      c.troncal?.responde == null ? 'var(--warning)'
                                     : c.troncal.responde ? 'var(--success)'    : 'var(--danger)' }}>
                          Troncal
                        </span>
                      ) : (
                        <span title={c.lineas
                          ? `${c.lineas_registradas} de ${c.lineas} línea(s) registrando ahora`
                          : 'Sin líneas que puedan registrar'}
                          style={{ fontSize:9, padding:'2px 6px', borderRadius:3, fontWeight:600,
                                   background: c.live_registered ? 'var(--success-bg)' : 'var(--danger-bg)',
                                   color:      c.live_registered ? 'var(--success)'    : 'var(--danger)' }}>
                          {c.live_registered ? 'Registra' : 'No registra'}
                        </span>
                      )}
                    </div>
                  </div>
                  {(CAPS.editDatos || CAPS.canToggle || CAPS.canDelete) && (
                    <div style={{ display:'flex', gap:6, marginTop:6 }} onClick={e => e.stopPropagation()}>
                      {CAPS.editDatos && (
                        <button onClick={e => openEdit(e, c)}
                          style={{ fontSize:10, padding:'2px 8px', borderRadius:4, border:'1px solid var(--border)',
                                   background:'var(--bg-raised)', color:'var(--text-secondary)', cursor:'pointer' }}>
                          Editar
                        </button>
                      )}
                      {CAPS.canToggle && (
                        <button onClick={e => handleToggleStatus(e, c)}
                          disabled={actionBusy === c.id}
                          style={{ fontSize:10, padding:'2px 8px', borderRadius:4, border:'1px solid var(--border)',
                                   background: c.status === 'active' ? 'var(--warning-bg)' : 'var(--success-bg)',
                                   color: c.status === 'active' ? 'var(--warning)' : 'var(--success)', cursor:'pointer' }}>
                          {actionBusy === c.id ? '…' : c.status === 'active' ? 'Desactivar' : 'Activar'}
                        </button>
                      )}
                      {CAPS.canDelete && (
                        <button onClick={e => openDelete(e, c)}
                          style={{ fontSize:10, padding:'2px 8px', borderRadius:4, border:'1px solid var(--danger)',
                                   background:'var(--danger-bg)', color:'var(--danger)', cursor:'pointer' }}>
                          Eliminar
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer lista — paginación */}
          <div style={{ padding:'6px 10px', borderTop:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              style={{ fontSize:11, padding:'3px 10px', borderRadius:4, border:'1px solid var(--border)',
                       background:'var(--bg-raised)', color:'var(--text-secondary)', cursor:'pointer',
                       opacity: page <= 1 ? 0.4 : 1 }}>
              ← Anterior
            </button>
            <span style={{ fontSize:10, color:'var(--text-muted)', textAlign:'center' }}>
              Pág. {page} / {totalPages} &nbsp;·&nbsp; {totalCount.toLocaleString()} clientes
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              style={{ fontSize:11, padding:'3px 10px', borderRadius:4, border:'1px solid var(--border)',
                       background:'var(--bg-raised)', color:'var(--text-secondary)', cursor:'pointer',
                       opacity: page >= totalPages ? 0.4 : 1 }}>
              Siguiente →
            </button>
          </div>
        </div>

        {/* Detalle */}
        {selected && (
          <div className="nv-card" style={{ padding:0, overflow:'hidden' }}>
            <ClienteDetail key={selected} id={selected} onClose={() => setSelected(null)} bancos={bancos} />
          </div>
        )}
      </div>

      {/* Modal editar cliente */}
      {editTarget && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
          onClick={e => { if(e.target===e.currentTarget) setEditTarget(null); }}>
          <div style={{ background:'var(--bg-surface)', borderRadius:'var(--r)', border:'1px solid var(--border)',
            padding:28, width:440, maxWidth:'90vw', boxShadow:'0 24px 64px rgba(0,0,0,0.4)' }}>
            <div style={{ fontSize:16, fontWeight:700, color:'var(--text-primary)', marginBottom:4 }}>
              Editar cliente
            </div>
            <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:20 }}>
              {editTarget.name} — el nombre se edita en la ficha, pestaña <strong>Datos</strong>.
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:11, color:'var(--text-muted)', display:'block', marginBottom:4 }}>
                Tipo de cliente
              </label>
              <select value={editForm.tipo_cliente||''}
                onChange={e => setEditForm(f => ({ ...f, tipo_cliente: e.target.value }))}
                style={{ width:'100%', padding:'8px 12px', boxSizing:'border-box',
                  background:'var(--bg-raised)', border:'1px solid var(--border)',
                  borderRadius:'var(--r-sm)', color:'var(--text-primary)', fontSize:13, outline:'none' }}>
                <option value="">— Sin definir —</option>
                {tiposCliente.map(t => (
                  <option key={t.codigo} value={t.codigo}>
                    {t.label}
                  </option>
                ))}
              </select>
              <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:4 }}>
                No se puede cambiar si el cliente ya tiene servicio del otro tipo.
              </div>
            </div>
            {[
              { label:'RUC / Cédula', key:'ruc', placeholder:'Ej: 0912345678001' },
              { label:'Teléfono', key:'phone', placeholder:'Ej: 0991234567' },
              { label:'Ciudad', key:'ciudad', placeholder:'Ej: Guayaquil' },
              { label:'Dirección física', key:'direccion', placeholder:'Ej: Av. 9 de Octubre 123' },
            ].map(({ label, key, placeholder }) => (
              <div key={key} style={{ marginBottom:12 }}>
                <label style={{ fontSize:11, color:'var(--text-muted)', display:'block', marginBottom:4 }}>{label}</label>
                <input type="text" value={editForm[key]||''} placeholder={placeholder}
                  onChange={e => setEditForm(f => ({ ...f, [key]: e.target.value }))}
                  style={{ width:'100%', padding:'8px 12px', boxSizing:'border-box',
                    background:'var(--bg-raised)', border:'1px solid var(--border)',
                    borderRadius:'var(--r-sm)', color:'var(--text-primary)', fontSize:13, outline:'none' }} />
              </div>
            ))}
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:11, color:'var(--text-muted)', display:'block', marginBottom:4 }}>Provincia</label>
              <select value={editForm.provincia||''} onChange={e => setEditForm(f => ({ ...f, provincia: e.target.value }))}
                style={{ width:'100%', padding:'8px 12px', boxSizing:'border-box',
                  background:'var(--bg-raised)', border:'1px solid var(--border)',
                  borderRadius:'var(--r-sm)', color:'var(--text-primary)', fontSize:13, outline:'none' }}>
                <option value="">— Sin provincia —</option>
                  <option value="AZUAY">AZUAY</option>
                  <option value="BOLIVAR">BOLIVAR</option>
                  <option value="CHIMBORAZO">CHIMBORAZO</option>
                  <option value="COTOPAXI">COTOPAXI</option>
                  <option value="EL ORO">EL ORO</option>
                  <option value="ESMERALDAS">ESMERALDAS</option>
                  <option value="GUAYAS">GUAYAS</option>
                  <option value="IMBABURA">IMBABURA</option>
                  <option value="LOJA">LOJA</option>
                  <option value="LOS RIOS">LOS RIOS</option>
                  <option value="MANABI">MANABI</option>
                  <option value="PASTAZA">PASTAZA</option>
                  <option value="PICHINCHA">PICHINCHA</option>
                  <option value="SANTA ELENA">SANTA ELENA</option>
                  <option value="SANTO DOMINGO">SANTO DOMINGO</option>
                  <option value="SUCUMBIOS">SUCUMBIOS</option>
                  <option value="TENA">TENA</option>
                  <option value="TUNGURAHUA">TUNGURAHUA</option>
              </select>
            </div>
            {editErr && <div style={{ padding:'8px 12px', background:'var(--danger-bg)', color:'var(--danger)',
              borderRadius:'var(--r-sm)', fontSize:12, marginBottom:12 }}>{editErr}</div>}
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button className="nv-btn nv-btn-ghost nv-btn-sm" onClick={() => setEditTarget(null)} disabled={editBusy}>Cancelar</button>
              <button className="nv-btn nv-btn-primary nv-btn-sm" onClick={handleSave} disabled={editBusy}>
                {editBusy ? <><span className="nv-spinner" /> Guardando...</> : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmar eliminación */}
      {deleteTarget && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
          onClick={e => { if(e.target===e.currentTarget) setDeleteTarget(null); }}>
          <div style={{ background:'var(--bg-surface)', borderRadius:'var(--r)', border:'1px solid var(--danger)',
            padding:28, width:400, maxWidth:'90vw', boxShadow:'0 24px 64px rgba(0,0,0,0.4)' }}>
            <div style={{ fontSize:16, fontWeight:700, color:'var(--danger)', marginBottom:8 }}>Eliminar cliente</div>
            <p style={{ fontSize:13, color:'var(--text-muted)', marginBottom:16 }}>
              Esta acción es <strong>permanente</strong>. Escribe el nombre del cliente para confirmar:
            </p>
            <div style={{ fontSize:12, fontWeight:600, color:'var(--text-primary)', marginBottom:10,
              padding:'6px 10px', background:'var(--bg-raised)', borderRadius:'var(--r-sm)', fontFamily:'var(--font-mono)' }}>
              {deleteTarget.name}
            </div>
            <input type="text" value={deleteInput} placeholder="Escribe el nombre exacto..."
              onChange={e => setDeleteInput(e.target.value)}
              style={{ width:'100%', padding:'8px 12px', boxSizing:'border-box', marginBottom:16,
                background:'var(--bg-raised)', border:'1px solid var(--border)',
                borderRadius:'var(--r-sm)', color:'var(--text-primary)', fontSize:13, outline:'none' }} />
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button className="nv-btn nv-btn-ghost nv-btn-sm" onClick={() => setDeleteTarget(null)} disabled={deleteBusy}>Cancelar</button>
              <button className="nv-btn nv-btn-sm" onClick={handleDelete} disabled={deleteBusy ||
                deleteInput.trim().toLowerCase() !== deleteTarget.name.trim().toLowerCase()}
                style={{ background:'var(--danger)', color:'#fff', border:'none',
                  opacity: deleteInput.trim().toLowerCase() === deleteTarget.name.trim().toLowerCase() ? 1 : 0.4 }}>
                {deleteBusy ? <><span className="nv-spinner" /> Eliminando...</> : 'Eliminar definitivamente'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal nuevo cliente */}
      {showCreate && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.55)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000,
        }} onClick={e => { if(e.target===e.currentTarget) setShowCreate(false); }}>
          <div style={{
            background:'var(--bg-surface)', borderRadius:'var(--r)', border:'1px solid var(--border)',
            padding:28, width:640, maxWidth:'92vw', boxShadow:'0 24px 64px rgba(0,0,0,0.4)',
            maxHeight:'90vh', overflowY:'auto',
          }}>
            <div style={{ fontSize:16, fontWeight:700, color:'var(--text-primary)', marginBottom:4 }}>Nuevo cliente</div>
            <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:20 }}>
              El cliente quedará en estado <strong>pendiente</strong> y deberá completar el proceso de onboarding.
            </div>
            {/* Empresa del cliente (clients.origin): decide qué usuarios de
                clientes.eneural.org lo ven. Con una sola empresa asignable no se pregunta. */}
            {empresasAlta.length > 1 && (
              <div style={{ marginBottom:18 }}>
                <label style={FORM_LABEL}>Empresa</label>
                <select value={createForm.empresa}
                  onChange={e => setCreateForm(f => ({ ...f, empresa: e.target.value }))}
                  style={{ width:'100%', marginTop:6, padding:'8px 10px', borderRadius:'var(--r)',
                           border:'1px solid var(--border)', background:'var(--bg-base)', color:'var(--text-primary)' }}>
                  {empresasAlta.length === EMPRESAS_CLIENTE.length
                    ? <option value="">Netvoice (por defecto)</option>
                    : <option value="" disabled>Elegí la empresa…</option>}
                  {empresasAlta.map(e => <option key={e.key} value={e.key}>{e.label}</option>)}
                </select>
              </div>
            )}
            {/* Tipo de cliente: solo descriptivo desde 2026-10-20 — NO decide si lleva línea
                o troncal; eso se elige después en Servicios → Asignar ANI. */}
            <div style={{ marginBottom:18 }}>
              <label style={FORM_LABEL}>Tipo de cliente *</label>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:10, marginTop:6 }}>
                {tiposCliente.map(t => {
                  const sel = createForm.tipo_cliente === t.codigo;
                  return (
                    <button
                      key={t.codigo}
                      type="button"
                      onClick={() => setCreateForm(f => ({ ...f, tipo_cliente: t.codigo }))}
                      style={{
                        textAlign:'left', padding:'10px 12px', cursor:'pointer',
                        borderRadius:'var(--r)',
                        border:`1px solid ${sel ? 'var(--accent)' : 'var(--border)'}`,
                        background: sel ? 'rgba(99,102,241,0.10)' : 'var(--bg-base)',
                        color:'var(--text-primary)',
                      }}
                    >
                      <div style={{ fontSize:13, fontWeight:600 }}>{t.label}</div>
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:6 }}>
                Define qué servicio se le puede asignar: una <strong>línea</strong> (1 DID, registro SIP)
                o una <strong>troncal</strong> (bloque de DIDs, autenticación por IP).
              </div>
            </div>
            {/* Campos en grid de 2 columnas, alineados por fila */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px 16px', alignItems:'start' }}>
              {[
                { label:'1er Nombre *',    key:'primer_nombre',    placeholder:'Ej: Juan' },
                { label:'2do Nombre',      key:'segundo_nombre',   placeholder:'Ej: Carlos' },
                { label:'1er Apellido *',  key:'primer_apellido',  placeholder:'Ej: Pérez' },
                { label:'2do Apellido',    key:'segundo_apellido', placeholder:'Ej: Gómez' },
                { label:'Razón social',    key:'razon_social',     placeholder:'Solo empresas. Ej: Empresa S.A.' },
                { label:'RUC / Cédula',    key:'ruc',              placeholder:'Ej: 0912345678001' },
                { label:'Email *',         key:'email',            placeholder:'contacto@empresa.com', type:'email' },
                { label:'Teléfono',        key:'phone',            placeholder:'Ej: 0991234567' },
              ].map(({ label, key, placeholder, type }) => (
                <div key={key}>
                  <label style={FORM_LABEL}>{label}</label>
                  <input
                    type={type || 'text'}
                    value={createForm[key]}
                    onChange={e => setCreateForm(f => ({ ...f, [key]: e.target.value }))}
                    placeholder={placeholder}
                    style={FORM_INPUT}
                  />
                </div>
              ))}
              {/* Provincia */}
              <div>
                <label style={FORM_LABEL}>Provincia *</label>
                <select
                  value={createForm.provincia}
                  onChange={e => setCreateForm(f => ({ ...f, provincia: e.target.value }))}
                  style={FORM_INPUT}
                >
                  <option value="">— Seleccione provincia —</option>
                  <option key="AZUAY" value="AZUAY">AZUAY</option>
                  <option key="BOLIVAR" value="BOLIVAR">BOLIVAR</option>
                  <option key="CHIMBORAZO" value="CHIMBORAZO">CHIMBORAZO</option>
                  <option key="COTOPAXI" value="COTOPAXI">COTOPAXI</option>
                  <option key="EL ORO" value="EL ORO">EL ORO</option>
                  <option key="ESMERALDAS" value="ESMERALDAS">ESMERALDAS</option>
                  <option key="GUAYAS" value="GUAYAS">GUAYAS</option>
                  <option key="IMBABURA" value="IMBABURA">IMBABURA</option>
                  <option key="LOJA" value="LOJA">LOJA</option>
                  <option key="LOS RIOS" value="LOS RIOS">LOS RIOS</option>
                  <option key="MANABI" value="MANABI">MANABI</option>
                  <option key="PASTAZA" value="PASTAZA">PASTAZA</option>
                  <option key="PICHINCHA" value="PICHINCHA">PICHINCHA</option>
                  <option key="SANTA ELENA" value="SANTA ELENA">SANTA ELENA</option>
                  <option key="SANTO DOMINGO" value="SANTO DOMINGO">SANTO DOMINGO</option>
                  <option key="SUCUMBIOS" value="SUCUMBIOS">SUCUMBIOS</option>
                  <option key="TENA" value="TENA">TENA</option>
                  <option key="TUNGURAHUA" value="TUNGURAHUA">TUNGURAHUA</option>
                </select>
              </div>
              {/* Ciudad */}
              <div>
                <label style={FORM_LABEL}>Ciudad</label>
                <input
                  type="text"
                  value={createForm.ciudad}
                  onChange={e => setCreateForm(f => ({ ...f, ciudad: e.target.value }))}
                  placeholder="Ej: Guayaquil"
                  style={FORM_INPUT}
                />
              </div>
              {/* Dirección — ocupa las 2 columnas */}
              <div style={{ gridColumn:'1 / -1' }}>
                <label style={FORM_LABEL}>Dirección física</label>
                <input
                  type="text"
                  value={createForm.direccion}
                  onChange={e => setCreateForm(f => ({ ...f, direccion: e.target.value }))}
                  placeholder="Ej: Av. 9 de Octubre 123 y Malecón"
                  style={FORM_INPUT}
                />
              </div>
              {/* Modo de pago */}
              <div>
                <label style={FORM_LABEL}>Modo de pago</label>
                <select
                  value={createForm.modo_pago}
                  onChange={e => setCreateForm(f => ({ ...f, modo_pago: e.target.value }))}
                  style={FORM_INPUT}
                >
                  <option value="caja">Caja</option>
                  <option value="debito_banco">Débito bancario</option>
                  <option value="debito_tarjeta">Débito tarjeta</option>
                  <option value="transferencia">Transferencia</option>
                </select>
              </div>
              {/* Mandato bancario — sólo si es débito bancario */}
              {createForm.modo_pago === 'debito_banco' && (
                <>
                  <div>
                    <label style={FORM_LABEL}>Banco</label>
                    <select
                      value={createForm.banco_codigo}
                      onChange={e => setCreateForm(f => ({ ...f, banco_codigo: e.target.value }))}
                      style={FORM_INPUT}
                    >
                      <option value="">— Seleccione banco —</option>
                      {bancos.map(b => (
                        <option key={b.codigo_bce} value={b.codigo_bce}>{b.nombre}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={FORM_LABEL}>Tipo de cuenta</label>
                    <select
                      value={createForm.tipo_cuenta}
                      onChange={e => setCreateForm(f => ({ ...f, tipo_cuenta: e.target.value }))}
                      style={FORM_INPUT}
                    >
                      <option value="AHO">Ahorros</option>
                      <option value="CTE">Corriente</option>
                    </select>
                  </div>
                  <div style={{ gridColumn:'1 / -1' }}>
                    <label style={FORM_LABEL}>N° de cuenta</label>
                    <input
                      value={createForm.numero_cuenta}
                      onChange={e => setCreateForm(f => ({ ...f, numero_cuenta: e.target.value }))}
                      placeholder="Se guarda cifrada"
                      style={FORM_INPUT}
                    />
                  </div>
                </>
              )}
            </div>
            {/* fin del grid de campos */}
            {createErr && (
              <div style={{ padding:'8px 12px', background:'var(--danger-bg)', color:'var(--danger)',
                borderRadius:'var(--r-sm)', fontSize:12, marginTop:16, marginBottom:0 }}>
                {createErr}
              </div>
            )}
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
              <button className="nv-btn nv-btn-ghost nv-btn-sm" onClick={() => setShowCreate(false)} disabled={creating}>
                Cancelar
              </button>
              <button className="nv-btn nv-btn-primary nv-btn-sm" onClick={handleCreate} disabled={creating}>
                {creating ? <><span className="nv-spinner" /> Creando...</> : 'Crear cliente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
